import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, DollarSign, Wallet, CreditCard, Clock, BarChart, CheckCircle, Percent, Check, Circle, Bot, Shield, Scale, RefreshCw, FileText, AlertTriangle, Search, Zap, Users, Info, History, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import RiskOverview from './RiskOverview';
import DiligenceReport from './DiligenceReport';
import ReputationRisk from './ReputationRisk';
import { normalizeFinancialExtraction } from '../lib/normalizeFinancialExtraction';
import { normalizeHeadcount, getHeadcountDisplayInfo } from '../lib/normalizeHeadcount';
import { documentsApi, dealsApi } from '../services/api';
import { useAnalysis } from '../contexts/AnalysisContext';
import { can } from '../lib/permissions';
import './AIFindings.css';

// Format currency values
function formatCurrency(value, decimals = 2) {
  if (value === null || value === undefined || isNaN(value)) return null;
  const num = Number(value);
  if (Math.abs(num) >= 1000000) {
    return `$${(num / 1000000).toFixed(decimals)}M`;
  } else if (Math.abs(num) >= 1000) {
    return `$${(num / 1000).toFixed(decimals)}K`;
  }
  return `$${num.toFixed(decimals)}`;
}

// Format percentage values
function formatPercent(value, decimals = 1) {
  if (value === null || value === undefined || isNaN(value)) return null;
  const num = Number(value);
  if (num > 1 && num <= 100) {
    return `${num.toFixed(decimals)}%`;
  }
  return `${(num * 100).toFixed(decimals)}%`;
}

// Format days
function formatDays(value) {
  if (value === null || value === undefined || isNaN(value)) return null;
  return `${Math.round(Number(value))} days`;
}

// Format based on unit type
function formatMetric(value, unit) {
  if (value === null || value === undefined) return 'Not found';
  switch (unit) {
    case 'currency': return formatCurrency(value);
    case 'percent': return formatPercent(value);
    case 'days': return formatDays(value);
    case 'number': return Number(value).toLocaleString();
    default: return String(value);
  }
}

// Clause categories from technical spec - IDs must match AI response keys
const CLAUSE_CATEGORIES = [
  { id: 'indemnification', label: 'Indemnification', icon: Shield, description: 'Mutual vs non-mutual clauses' },
  { id: 'liability_caps', label: 'Liability Caps', icon: Scale, description: 'Limitation of liability caps' },
  { id: 'change_of_control', label: 'Change of Control', icon: RefreshCw, description: 'Change of control restrictions' },
  { id: 'assignment', label: 'Assignment', icon: FileText, description: 'Assignment restrictions' },
  { id: 'termination_asymmetry', label: 'Termination Asymmetry', icon: AlertTriangle, description: 'Termination clause imbalances' },
  { id: 'unilateral_pricing', label: 'Unilateral Pricing', icon: DollarSign, description: 'Unilateral pricing rights' },
  { id: 'audit_rights', label: 'Audit Rights', icon: Search, description: 'Audit and inspection rights' },
  { id: 'injunctive_relief', label: 'Injunctive Relief', icon: Zap, description: 'Injunctive relief provisions' },
  { id: 'auto_renewals', label: 'Auto-Renewals', icon: RefreshCw, description: 'Automatic renewal clauses' },
  { id: 'mfn', label: 'MFN Clauses', icon: Users, description: 'Most Favored Nation terms' },
];

// Financial metrics config for data coverage
const FINANCIAL_METRICS_CONFIG = [
  { key: 'revenue', label: 'Revenue' },
  { key: 'ebitda', label: 'EBITDA' },
  { key: 'ebitda_reported', label: 'Reported EBITDA' },
  { key: 'ebitda_management_adjusted', label: 'Management Adjusted EBITDA' },
  { key: 'ebitda_seller_adjusted', label: 'Seller Adjusted EBITDA' },
  { key: 'ebitda_qoe_adjusted', label: 'QoE Adjusted EBITDA' },
  { key: 'ebitda_pro_forma', label: 'Pro Forma Synergized EBITDA' },
  { key: 'net_debt', label: 'Net Debt' },
  { key: 'revolver_availability', label: 'Revolver Availability' },
  { key: 'gross_profit', label: 'Gross Profit' },
  { key: 'gross_margin', label: 'Gross Margin' },
  { key: 'dso', label: 'DSO' },
  { key: 'customer_concentration', label: 'Customer Concentration' },
  { key: 'lease_expense', label: 'Lease Expense' },
  { key: 'insurance_expense', label: 'Insurance Expense' },
  { key: 'it_expense', label: 'IT Expense' },
  { key: 'overtime_payroll_pct', label: 'Overtime % of Payroll' },
  { key: 'customer_count', label: 'Customer Count' },
];

function AIFindings({ selectedDeal, currentUser }) {
  const canEdit = can(currentUser, 'edit');
  const { state: analysisState, startAnalysis, clearResult } = useAnalysis();

  const [deals, setDeals] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [analysisMode, setAnalysisMode] = useState('deal'); // 'deal' or 'document'
  const [modelMode, setModelMode] = useState('hybrid'); // 'sonnet' | 'hybrid' | 'gemini' | 'deepseek'
  const [cleaningOn, setCleaningOn] = useState(false); // financial data cleaning toggle
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [findings, setFindings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzingStage, setAnalyzingStage] = useState('');
  const [analysisError, setAnalysisError] = useState(null);
  const [activeTab, setActiveTab] = useState('report'); // 'report', 'clauses', 'reputation'
  // eslint-disable-next-line no-unused-vars
  const [expandedEvidence, setExpandedEvidence] = useState({});
  const [expandedCoverage, setExpandedCoverage] = useState({ found: false, missing: false });
  
  // Overtime leakage classification toggle (Prompt 6)
  const [otClassification, setOtClassification] = useState('standalone'); // 'standalone' or 'integration'
  
  // Analysis history
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistoryId, setLoadingHistoryId] = useState(null);
  const [activeHistoryId, setActiveHistoryId] = useState(null); // null = latest

  // Diligence intake modal
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [intakeData, setIntakeData] = useState(null);

  // Headcount functions expand/collapse
  const [showAllFunctions, setShowAllFunctions] = useState(false);
  const [expandAdjustments, setExpandAdjustments] = useState(false);

  // Clause → finding note state
  const [addingNoteForClause, setAddingNoteForClause] = useState(null);
  const [clauseNoteAdded, setClauseNoteAdded] = useState({});

  // DD report text selection → finding note modal
  const [ddNoteModal, setDdNoteModal] = useState(null); // { text }
  const [ddNoteForm, setDdNoteForm] = useState({ date: '', category: '', question: '', finding: '', sourceDocument: 'DD Report' });
  const [ddNoteSaving, setDdNoteSaving] = useState(false);

  const DD_NOTE_CATEGORIES = [
    'Clients & Revenue',
    'Front Office (Sales & Delivery)',
    'Operations & Back Office',
    'Legal & Corporate',
    'Finance, Accounting, Tax, QoE, NWC',
    'Valuation/Terms Items',
    'Integration Items',
    'Miscellaneous',
  ];

  const openDdNoteModal = (selectedText) => {
    setDdNoteForm({
      date: new Date().toISOString().split('T')[0],
      category: 'Miscellaneous',
      question: '',
      finding: selectedText,
      sourceDocument: 'DD Report',
    });
    setDdNoteModal({ text: selectedText });
  };

  const saveDdNote = async (e) => {
    e.preventDefault();
    if (!selectedTarget) return;
    setDdNoteSaving(true);
    try {
      await dealsApi.addNote(selectedTarget, ddNoteForm);
      setDdNoteModal(null);
    } catch (err) {
      console.error('Failed to save DD note:', err);
    } finally {
      setDdNoteSaving(false);
    }
  };

  // Toggle evidence visibility
  // eslint-disable-next-line no-unused-vars
  const toggleEvidence = (key) => {
    setExpandedEvidence(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Load deals and documents on mount
  useEffect(() => {
    fetchDeals();
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When selectedDeal prop changes, switch to deal mode and select it
  useEffect(() => {
    if (selectedDeal) {
      setAnalysisMode('deal');
      setSelectedTarget(selectedDeal.id);
    }
  }, [selectedDeal]);

  // Load findings when target changes
  useEffect(() => {
    if (selectedTarget) {
      setFindings(null);
      setActiveTab('report');
      setAnalysisHistory([]);
      setShowHistory(false);
      setActiveHistoryId(null);
      fetchFindings();
      if (analysisMode === 'deal') fetchAnalysisHistory(selectedTarget);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTarget, analysisMode]);

  // Sync global analysis state → local component state
  useEffect(() => {
    const s = analysisState;
    const isForThisDeal = s.dealId === selectedTarget && analysisMode === 'deal';

    if (isForThisDeal) {
      const isRunning = s.status !== 'idle' && s.status !== 'complete' && s.status !== 'error';
      setAnalyzing(isRunning);
      setAnalyzingStage(isRunning ? (s.stage || 'Analyzing...') : '');

      if (s.status === 'complete' && s.result) {
        setFindings(transformFindings(s.result));
        setAnalysisError(null);
        fetchAnalysisHistory(selectedTarget);
        clearResult(selectedTarget);
      } else if (s.status === 'error') {
        setAnalysisError(s.error || 'Analysis failed. Please try again.');
        setAnalyzing(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisState, selectedTarget, analysisMode]);

  const fetchDeals = async () => {
    try {
      const response = await dealsApi.list();
      const data = response.data;
      setDeals(data);
      // Auto-select first deal if none selected
      if (data.length > 0 && !selectedTarget && analysisMode === 'deal') {
        setSelectedTarget(data[0].id);
      }
    } catch (error) {
      console.error('Error fetching deals:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await documentsApi.list();
      const data = response.data;
      const docs = data.documents || data || [];
      setDocuments(docs);
      if (docs.length > 0 && !selectedTarget && analysisMode === 'document') {
        setSelectedTarget(docs[0].jobId);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchFindings = async () => {
    if (!selectedTarget) return;
    
    setLoading(true);
    try {
      const response = analysisMode === 'deal' 
        ? await dealsApi.getFindings(selectedTarget)
        : await documentsApi.getFindings(selectedTarget);
      const data = response.data;
      
      console.log(`[AIFindings] Fetched data for ${data.dealName || selectedTarget}:`, {
        revenue: data.financial_data?.revenue,
        dealId: data.dealId,
        dealName: data.dealName,
        analyzedAt: data.analyzedAt,
      });
      console.log('[AIFindings] analyzedAt value:', data.analyzedAt);
      
      // Also fetch profile data if in deal mode
      let profileData = null;
      if (analysisMode === 'deal') {
        try {
          const profileResponse = await dealsApi.get(selectedTarget);
          if (profileResponse.data) {
            profileData = profileResponse.data;
          }
        } catch (e) {
          // Profile may not exist yet
        }
      }
      
      // Merge profile operational data if findings data is empty
      if (profileData && analysisMode === 'deal') {
        console.log('[AIFindings] Profile data available:', profileData);
        const op = data.operational_data || data.operational_extraction || {};
        // Check if there's ACTUAL data (not just empty arrays/nulls)
        const hasHeadcount = typeof op.headcount?.total === 'number' && op.headcount.total > 0;
        const hasOffices = Array.isArray(op.offices) && op.offices.length > 0 && op.offices.some(o => o.city || o.metro);
        const hasSaas = Array.isArray(op.saas_stack) && op.saas_stack.length > 0;
        const hasVendors = Array.isArray(op.vendors) && op.vendors.length > 0;
        
        const hasRealOpData = hasHeadcount || hasOffices || hasSaas || hasVendors;
        console.log('[AIFindings] Has real op data:', hasRealOpData, {hasHeadcount, hasOffices, hasSaas, hasVendors});
        
        if (!hasRealOpData) {
          console.log('[AIFindings] Merging profile data into operational_data');
          data.operational_data = profileToOperationalData(profileData);
        }
      }
      
      // Transform backend data to component format
      setFindings(transformFindings(data));
    } catch (error) {
      console.error('Error fetching findings:', error);
      setFindings(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Convert profile format to operational_data format
  const profileToOperationalData = (profile) => {
    // Normalize headcount to canonical model
    const headcount = normalizeHeadcount(profile.headcount || profile.headcountByFunction);
    const displayInfo = getHeadcountDisplayInfo(headcount);
    
    return {
      headcount: {
        total: displayInfo.value,
        by_function: headcount.byFunction || [],
        warning: displayInfo.warning,
        hasAmbiguity: headcount.ambiguity?.multipleTotalsFound,
        candidates: headcount.ambiguity?.candidates
      },
      offices: profile.offices || [],
      saas_stack: profile.saasStack || [],
      vendors: profile.vendors || [],
      customer_count: profile.customers?.reduce((sum, c) => sum + (c.count || 0), 0) || null
    };
  };

  const addClauseToNotes = async (clause, clauseLabel) => {
    if (!selectedTarget || analysisMode !== 'deal') return;
    setAddingNoteForClause(clause.type);
    try {
      const snippetText = clause.snippets?.[0]?.text || '';
      const location = clause.snippets?.[0]
        ? `Page ${clause.snippets[0].page || '?'}${clause.snippets[0].section ? `, ${clause.snippets[0].section}` : ''}`
        : '';
      const content = [
        `**Clause Finding: ${clauseLabel}**`,
        clause.risk ? `Risk level: ${clause.risk}` : '',
        snippetText ? `Excerpt: "${snippetText.substring(0, 300)}"` : '',
        location ? `Location: ${location}` : '',
        clause.confidence ? `Confidence: ${Math.round(clause.confidence * 100)}%` : '',
      ].filter(Boolean).join('\n');
      await dealsApi.addNote(selectedTarget, { content, type: 'clause_finding' });
      setClauseNoteAdded(prev => ({ ...prev, [clause.type]: true }));
      setTimeout(() => setClauseNoteAdded(prev => ({ ...prev, [clause.type]: false })), 2500);
    } catch (e) {
      console.error('Failed to add clause note:', e);
    } finally {
      setAddingNoteForClause(null);
    }
  };

  const openIntakeModal = () => {
    const deal = deals.find(d => d.id === selectedTarget);
    setIntakeData(buildIntakeDefaults(deal));
    setShowIntakeModal(true);
  };

  const runAnalysis = async (params = {}) => {
    if (!selectedTarget) return;
    setShowIntakeModal(false);
    setAnalysisError(null);

    if (analysisMode === 'deal') {
      const deal = deals.find(d => d.id === selectedTarget);
      // Kick off in global context — survives page navigation
      startAnalysis(selectedTarget, deal?.name || '', { ...params, analysisMode: modelMode, cleaningMode: cleaningOn ? 'full' : 'off' });
    } else {
      // Document mode still runs locally (fast, no need for bg)
      setAnalyzing(true);
      setAnalyzingStage('Analyzing...');
      try {
        const response = await documentsApi.analyze(selectedTarget);
        setFindings(transformFindings(response.data));
      } catch (error) {
        console.error('Error running document analysis:', error);
        setAnalysisError(error.message || 'Analysis failed. Please try again.');
      } finally {
        setAnalyzing(false);
        setAnalyzingStage('');
      }
    }
  };

  const fetchAnalysisHistory = async (dealId) => {
    try {
      const res = await dealsApi.getAnalysisHistory(dealId);
      setAnalysisHistory(res.data || []);
    } catch {}
  };

  const loadHistoricAnalysis = async (dealId, analysisId) => {
    setLoadingHistoryId(analysisId);
    try {
      const res = await dealsApi.getAnalysisById(dealId, analysisId);
      setFindings(transformFindings({ ...res.data.analysis, documentCount: res.data.doc_count, analyzedAt: res.data.created_at }));
      setActiveHistoryId(analysisId);
      setShowHistory(false);
    } catch (e) {
      console.error('Failed to load historic analysis:', e);
    } finally {
      setLoadingHistoryId(null);
    }
  };

  // Extract score from executive summary text (e.g., "High Risk (68/100)")
  const extractScoreFromSummary = (summary) => {
    if (!summary) return null;
    const match = summary.match(/\((\d+)\/100\)/);
    return match ? parseInt(match[1], 10) : null;
  };

  // Transform backend findings to component format
  const transformFindings = (data) => {
    if (!data) return null;

    // Simplified: only dd_report and detected_clauses
    // Claude returns wrapped { dd_report: {...} }; Gemini often returns flat { flags: [...], ... }
    const ddReport = data.dd_report || (data.flags || data.executive_summary_paragraphs ? data : null);
    const documentCount = data.documentCount || ddReport?.documents_reviewed?.length || 0;

    const rawClauseData = data.detected_clauses || data.clauses || {};
    const detectedClauses = Array.isArray(rawClauseData)
      ? rawClauseData
      : Object.entries(rawClauseData).map(([key, value]) => ({
          type: key,
          detected: value.detected || false,
          risk: value.risk || null,
          confidence: value.confidence || 0,
          snippets: value.snippets || [],
        }));

    return {
      documentCount,
      detectedClauses,
      ddReport,
      analyzedAt: data.analyzedAt || data.analyzed_at || null,
      // keep legacy fields so history/other code doesn't break
      riskLevel: 'not_analyzed',
      riskScore: 0,
    };
  };

  // LEGACY: kept so history code doesn't break — no longer used in main flow
  const _transformFindingsLegacy = (data) => {
    if (!data) return null;
    const riskLevel = (data.overallRisk || data.risk_assessment?.overall_risk_level || 'NOT_ANALYZED').toLowerCase();
    
    // Prioritize score from executive summary (AI's stated score) over calculated risk_score
    const executiveSummary = data.executive_summary || data.executiveSummary || data.summary;
    const scoreFromSummary = extractScoreFromSummary(executiveSummary);
    const riskScore = data.riskScore || data.risk_assessment?.risk_score || 0;
    
    // Normalize risk score to 0-100 scale, preferring score from summary
    const normalizedRiskScore = scoreFromSummary !== null ? scoreFromSummary : Math.round(riskScore * 100);
    
    // Extract confidence score
    const confidenceScore = data.confidence_avg || 
      (data.confidence_scores ? 
        Object.values(data.confidence_scores).reduce((a, b) => a + b, 0) / Object.values(data.confidence_scores).length 
        : 0);
    
    // Transform risks
    const redRisks = data.highRisk || [];
    const orangeRisks = data.mediumRisk || [];
    const lowRisks = data.lowRisk || [];
    
    // Extract clause detections - supports both object format (new) and array format (legacy)
    const rawClauseData = data.detected_clauses || data.clauses || {};
    // Transform object format to array format expected by UI
    const detectedClauses = Array.isArray(rawClauseData) 
      ? rawClauseData 
      : Object.entries(rawClauseData).map(([key, value]) => ({
          type: key,
          category: key,
          detected: value.detected || false,
          risk: value.risk || null,
          confidence: value.confidence || 0,
          snippets: value.snippets || [],
          location: value.snippets?.[0]?.page 
            ? `Page ${value.snippets[0].page}${value.snippets[0].section ? `, ${value.snippets[0].section}` : ''}`
            : 'Unknown'
        }));
    
    // Extract financial data
    const financialData = data.financial_data || data.financial || {};
    
    // Debug logging for financial data
    console.log('financialExtraction raw', data.financial_data || data.financial);
    console.log('financialExtraction normalized', financialData);
    
    // Extract operational data
    const operationalData = data.operational_data || data.operational || data.operational_extraction || {};
    
    return {
      riskLevel,
      riskScore: normalizedRiskScore,
      rawRiskScore: riskScore,
      confidenceScore,
      documentCount: data.documentCount || 0,
      executiveSummary: data.executive_summary || data.executiveSummary || data.summary,
      category_scores: data.category_scores || data.categoryScores || [],
      
      highRisks: redRisks.map(r => typeof r === 'string' ? { title: r, detail: '' } : { title: r.title || 'Unknown Risk', detail: r.detail || '', tags: r.tags || [], sourceDocument: r.sourceDocument }),
      mediumRisks: orangeRisks.map(r => typeof r === 'string' ? { title: r, detail: '' } : { title: r.title || 'Unknown Risk', detail: r.detail || '', tags: r.tags || [], sourceDocument: r.sourceDocument }),
      lowRisks: lowRisks.map(r => typeof r === 'string' ? { title: r, detail: '' } : { title: r.title || 'Unknown Risk', detail: r.detail || '', tags: r.tags || [], sourceDocument: r.sourceDocument }),
      greenFlags: (data.key_findings || data.greenFlags || []).map(f => typeof f === 'string' ? { title: f, detail: f } : { title: f.title || 'Positive Finding', detail: f.detail || f.finding || '', tags: f.tags || [], sourceDocument: f.sourceDocument }),
      
      detectedClauses: detectedClauses.map(c => ({
        type: c.type,
        category: c.category,
        location: c.location || 'Unknown',
        text: c.text || c.excerpt || (c.snippets?.[0]?.text) || '',
        confidence: c.confidence || 0.8,
        riskIndication: c.risk_indication || c.risk || 'neutral',
        detected: c.detected || false,
        snippets: c.snippets || []
      })),
      
      // Use the shared normalization utility
      financial: (() => {
        const normalized = normalizeFinancialExtraction(financialData);
        
        // Map to the expected format for the UI
        // Use Base EBITDA for all calculations (unified definition)
        const baseEbitda = normalized.ebitda.base;
        const ebitdaForDisplay = baseEbitda?.value !== null ? baseEbitda.value : normalized.ebitda.value;
        const ebitdaTypeForDisplay = baseEbitda?.type || normalized.ebitda.variant || 'Unknown';
        
        return {
          revenue: normalized.revenue.value,
          revenuePeriod: normalized.revenue.period,
          // Use Base EBITDA as the unified EBITDA value
          ebitda: ebitdaForDisplay,
          ebitdaPeriod: baseEbitda?.period || normalized.ebitda.period,
          ebitdaVariant: ebitdaTypeForDisplay,
          ebitdaType: baseEbitda?.type || 'Unknown',
          baseEbitda: baseEbitda,  // Full base EBITDA object for reference
          grossProfit: normalized.grossProfit.value,
          grossProfitPeriod: normalized.grossProfit.period,
          grossMargin: normalized.grossMargin.value,
          grossMarginPeriod: normalized.grossMargin.period,
          grossMarginCalculated: normalized.grossMargin.calculated,
          customerCount: normalized.customerCount.value,
          netDebt: normalized.netDebt.value,
          netDebtPeriod: normalized.netDebt.period,
          revolverAvailability: normalized.revolverAvailability.value,
          revolverPeriod: normalized.revolverAvailability.period,
          dso: normalized.dso.value,
          dsoPeriod: normalized.dso.period,
          overtimePctPayroll: normalized.overtimePctPayroll.value,
          leaseExpense: normalized.leaseExpense.value,
          leasePeriod: normalized.leaseExpense.period,
          insuranceExpense: normalized.insuranceExpense.value,
          insurancePeriod: normalized.insuranceExpense.period,
          itExpense: normalized.itExpense.value,
          itPeriod: normalized.itExpense.period,
          customerConcentration: {
            top_1: normalized.customerConcentration.value,
            label: normalized.customerConcentration.label
          },
          customerConcentrationPeriod: normalized.customerConcentration.period,
          ebitdaVariants: normalized.ebitda.variants,
          // Keep raw arrays for detailed views
          revenuePeriods: normalized.revenuePeriods,
          ebitdaPeriods: normalized.ebitda.variants,
          grossProfitPeriods: normalized.grossProfitPeriods,
          grossMarginPeriods: normalized.grossMarginPeriods
        };
      })(),
      
      // EBITDA Bridge from PROMPT 2 analysis
      ebitdaBridge: data.ebitdaBridge || null,
      
      operational: (() => {
        // Support both old snake_case and new camelCase schema
        const op = operationalData;
        
        // Debug logging - show actual structure
        console.log('operational raw keys:', Object.keys(op));
        console.log('operational raw:', JSON.stringify(op, null, 2));
        
        // Normalize headcount data
        const headcount = op.headcount || op.head_count || null;
        // Only use headcount as fallback if it's a number (not the whole object)
        const headcountTotal = headcount?.total ?? (typeof headcount === 'number' ? headcount : null);
        const headcountByFunction = headcount?.by_function || headcount?.byFunction || op.headcount_by_function || [];
        
        // Normalize office locations
        const officeLocations = op.offices || op.office_locations || op.locations || [];
        
        // Normalize SaaS stack
        const saasStack = op.saas_stack || op.saasStack || op.saas || [];
        
        // Normalize vendors
        const vendors = op.vendors || op.vendor_list || op.suppliers || [];
        
        const normalized = {
          headcount: {
            total: headcountTotal,
            byFunction: Array.isArray(headcountByFunction) ? headcountByFunction : []
          },
          offices: Array.isArray(officeLocations) ? officeLocations : [],
          saasStack: Array.isArray(saasStack) ? saasStack : [],
          vendors: Array.isArray(vendors) ? vendors.slice(0, 20) : [], // Limit to 20
          customerCount: op.customer_count || op.customerCount || null
        };
        
        console.log('operational normalized', normalized);
        return normalized;
      })(),
      
      timeline: [
        { label: 'Upload', status: 'complete' },
        { label: 'Classification', status: data.overallRisk ? 'complete' : 'pending' },
        { label: 'Extraction', status: data.overallRisk ? 'complete' : 'pending' },
        { label: 'Risk Detection', status: data.overallRisk ? 'complete' : 'pending' },
        { label: 'Quantification', status: data.overallRisk ? 'complete' : 'pending' },
        { label: 'Review', status: data.overallRisk ? 'in-progress' : 'pending' },
      ],

      ddReport: data.dd_report || null,
      analyzedAt: data.analyzedAt || data.analyzed_at || null,

      raw: data
    };
  };

  const getTargetName = () => {
    if (analysisMode === 'deal') {
      const deal = deals.find(d => d.id === selectedTarget);
      return deal?.name || 'Select a deal';
    } else {
      const doc = documents.find(d => (d.id || d.jobId) === selectedTarget);
      return doc?.filename || 'Select a document';
    }
  };

  const getRiskBadge = () => {
    if (!findings || findings.riskLevel === 'not_analyzed') {
      return { text: 'NOT ANALYZED', class: 'not-analyzed', color: '#9CA3AF' };
    }
    switch (findings.riskLevel) {
      case 'high':
      case 'critical':
        return { text: 'HIGH RISK', class: 'high', color: '#DC2626' };
      case 'moderate':
      case 'medium':
        return { text: 'MODERATE RISK', class: 'moderate', color: '#D97706' };
      case 'low':
      case 'green':
        return { text: 'LOW RISK', class: 'low', color: '#059669' };
      default:
        return { text: 'MODERATE RISK', class: 'moderate', color: '#D97706' };
    }
  };

  // EBITDA Bridge Card component for Financial Data tab
  const EbitdaBridgeCard = ({ bridge, netDebt, revenue }) => {
    // Extract data for leverage calculation (before early return)
    const steps = bridge?.bridge || [];
    const qoeAdjustedEbitda = steps.find(s => s.step === 'QoE Adjusted EBITDA')?.value;
    const proFormaEbitda = steps.find(s => s.step === 'Pro Forma Stabilized EBITDA')?.value;
    
    // Calculate leverage impact
    const leverageImpact = React.useMemo(() => {
      if (!netDebt || !qoeAdjustedEbitda || !proFormaEbitda) return null;
      if (qoeAdjustedEbitda <= 0 || proFormaEbitda <= 0) return null;
      
      const baseLeverage = netDebt / qoeAdjustedEbitda;
      const proFormaLeverage = netDebt / proFormaEbitda;
      const deltaTurns = proFormaLeverage - baseLeverage;
      
      let commentary = 'Neutral';
      let chipClass = 'neutral';
      
      if (proFormaLeverage > 4.5) {
        commentary = 'High financing risk';
        chipClass = 'high-risk';
      } else if (deltaTurns <= -0.5) {
        commentary = 'Value accretive';
        chipClass = 'positive';
      } else if (deltaTurns > 0) {
        commentary = 'Risk-enhancing';
        chipClass = 'negative';
      }
      
      return {
        baseLeverage,
        proFormaLeverage,
        deltaTurns,
        commentary,
        chipClass
      };
    }, [netDebt, qoeAdjustedEbitda, proFormaEbitda]);
    
    if (!bridge?.bridge) return null;
    
    const { period, flags, notes } = bridge;
    
    // Calculate net adjustments for display
    const sellerAdjustments = bridge.adjustments?.seller || [];
    const qoeAdjustments = bridge.adjustments?.qoe || [];
    
    return (
      <div className="financial-card-ic">
        <h4 className="card-title-ic">
          <BarChart size={18} />
          EBITDA Bridge
          {period && <span className="bridge-period-tag">{period}</span>}
        </h4>
        
        {/* Bridge Steps Table */}
        <table className="ebitda-bridge-table">
          <thead>
            <tr>
              <th>Step</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {steps.map((step, idx) => (
              <tr key={idx} className={step.value === null ? 'empty' : ''}>
                <td className="step-name">{step.step}</td>
                <td className="step-value">{formatCurrency(step.value)}</td>
              </tr>
            ))}
            
            {/* EBITDA Margin Row - Derived Metric */}
            <tr className="derived-metric-row">
              <td className="step-name">
                <span className="derived-label">EBITDA Margin (Base)</span>
              </td>
              <td className="step-value">
                {(() => {
                  const rev = revenue;
                  const baseEbitda = qoeAdjustedEbitda || steps.find(s => s.step === 'Seller Adjusted EBITDA')?.value || steps.find(s => s.step === 'Reported EBITDA')?.value;
                  if (!rev || !baseEbitda) {
                    return <span className="derived-unavailable">Not available</span>;
                  }
                  const margin = baseEbitda / rev;
                  return (
                    <span className="derived-value">
                      {(margin * 100).toFixed(1)}%
                      <span className="derived-subtitle">(Base EBITDA ÷ Revenue)</span>
                    </span>
                  );
                })()}
              </td>
            </tr>

            {/* Net Leverage Row - Derived Metric */}
            <tr className="derived-metric-row">
              <td className="step-name">
                <span className="derived-label">Net Leverage (Base)</span>
              </td>
              <td className="step-value">
                {(() => {
                  if (!netDebt || !qoeAdjustedEbitda) {
                    return <span className="derived-unavailable">Not available (Net Debt not found)</span>;
                  }
                  const leverage = netDebt / qoeAdjustedEbitda;
                  return (
                    <span className="derived-value">
                      {leverage.toFixed(1)}x
                      <span className="derived-subtitle">(Net Debt ÷ Base EBITDA)</span>
                    </span>
                  );
                })()}
              </td>
            </tr>

            {/* Pro Forma Leverage Row - Derived Metric */}
            <tr className="derived-metric-row">
              <td className="step-name">
                <span className="derived-label">Pro Forma Leverage</span>
              </td>
              <td className="step-value">
                {(() => {
                  if (!netDebt || !proFormaEbitda) {
                    return <span className="derived-unavailable">Not available</span>;
                  }
                  const leverage = netDebt / proFormaEbitda;
                  return (
                    <span className="derived-value">
                      {leverage.toFixed(1)}x
                      <span className="derived-subtitle">(Net Debt ÷ Pro Forma EBITDA)</span>
                    </span>
                  );
                })()}
              </td>
            </tr>

            {/* Delta Leverage Row - Derived Metric */}
            <tr className="derived-metric-row">
              <td className="step-name">
                <span className="derived-label">Δ Leverage (turns)</span>
                {leverageImpact && (
                  <span className={`leverage-chip ${leverageImpact.chipClass}`}>
                    {leverageImpact.commentary}
                  </span>
                )}
              </td>
              <td className="step-value">
                {leverageImpact ? (
                  <span className={`derived-value ${leverageImpact.deltaTurns < 0 ? 'positive' : leverageImpact.deltaTurns > 0 ? 'negative' : ''}`}>
                    {leverageImpact.deltaTurns > 0 ? '+' : ''}{leverageImpact.deltaTurns.toFixed(1)}x
                    <span className="derived-subtitle">(Pro Forma − Net Leverage)</span>
                  </span>
                ) : (
                  <span className="derived-unavailable">
                    Not available {!netDebt ? '(Net Debt not found)' : '(EBITDA data incomplete)'}
                  </span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
        
        {/* Flags */}
        {flags && (flags.adjustmentsExceed25Pct || flags.recurringCostsRepeatAcrossYears || flags.ownerCompExceeds15Pct) && (
          <div className="bridge-flags-mini">
            <h5>Quality Flags</h5>
            <div className="flags-list-mini">
              {flags.adjustmentsExceed25Pct && (
                <span className="flag-chip warning">Adjustments exceed 25%</span>
              )}
              {flags.recurringCostsRepeatAcrossYears && (
                <span className="flag-chip warning">Recurring costs repeat</span>
              )}
              {flags.ownerCompExceeds15Pct && (
                <span className="flag-chip warning">Owner comp &gt; 15%</span>
              )}
            </div>
          </div>
        )}
        
        {/* Adjustments Summary */}
        {(sellerAdjustments.length > 0 || qoeAdjustments.length > 0) && (
          <div className="bridge-adjustments-mini">
            <h5>Key Adjustments</h5>
            <div className="adjustments-lists">
              {(expandAdjustments ? sellerAdjustments : sellerAdjustments.slice(0, 3)).map((adj, idx) => (
                <div key={idx} className="adjustment-item-mini">
                  <span className="adj-label">{adj.label}</span>
                  <span className={`adj-amount ${adj.amount >= 0 ? 'positive' : 'negative'}`}>
                    {adj.amount >= 0 ? '+' : '-'}{formatCurrency(Math.abs(adj.amount))}
                  </span>
                  <span className={`adj-class ${adj.classification?.replace(/\s/g, '-').toLowerCase()}`}>
                    {adj.classification}
                  </span>
                </div>
              ))}
              {sellerAdjustments.length > 3 && (
                <button 
                  className="more-adjustments"
                  onClick={() => setExpandAdjustments(!expandAdjustments)}
                >
                  {expandAdjustments ? 'Show less' : `+${sellerAdjustments.length - 3} more`}
                </button>
              )}
            </div>
          </div>
        )}
        
        {notes && (
          <div className="bridge-notes-mini">
            <p>{notes}</p>
          </div>
        )}
      </div>
    );
  };

  // Customer Concentration Shock Model calculation (PROMPT 5)
  const computeCustomerConcentrationShock = (params) => {
    const {
      largestCustomerPctRevenue,
      revenue,
      ebitda,
      ebitdaMarginOptional,
      hasMsa,
      hasAssignmentClause
    } = params;
    
    // Check if we have required values
    if (largestCustomerPctRevenue === null || largestCustomerPctRevenue === undefined || !revenue) {
      return {
        canCompute: false,
        reasonIfCannotCompute: "Insufficient detail (largest customer % not identified)",
        largestCustomerPctRevenue: null,
        ebitdaMarginUsed: null,
        ebitdaAtRiskDollar: null,
        ebitdaAtRiskPct: null,
        severity: null,
        modifiers: { noMsa: false, noAssignment: false }
      };
    }
    
    // Ensure largestCustomerPctRevenue is in decimal form (0.26 not 26)
    const customerPct = largestCustomerPctRevenue > 1 ? largestCustomerPctRevenue / 100 : largestCustomerPctRevenue;
    
    // Calculate EBITDA margin
    let ebitdaMargin = ebitdaMarginOptional;
    if (ebitdaMargin === null || ebitdaMargin === undefined) {
      if (ebitda && revenue) {
        ebitdaMargin = ebitda / revenue;
      } else {
        return {
          canCompute: false,
          reasonIfCannotCompute: "EBITDA or EBITDA margin not available",
          largestCustomerPctRevenue: customerPct,
          ebitdaMarginUsed: null,
          ebitdaAtRiskDollar: null,
          ebitdaAtRiskPct: null,
          severity: null,
          modifiers: { noMsa: false, noAssignment: false }
        };
      }
    } else if (ebitdaMargin > 1) {
      // Normalize if provided as percentage
      ebitdaMargin = ebitdaMargin / 100;
    }
    
    // Calculate EBITDA at risk
    const ebitdaAtRiskDollar = customerPct * ebitdaMargin * revenue;
    
    // Calculate % of EBITDA at risk
    let ebitdaAtRiskPct = null;
    if (ebitda && Math.abs(ebitda) > 0) {
      ebitdaAtRiskPct = ebitdaAtRiskDollar / Math.abs(ebitda);
    }
    
    // Determine base severity from thresholds
    let severity = "Moderate";
    if (ebitdaAtRiskPct !== null) {
      if (ebitdaAtRiskPct > 0.20) {
        severity = "Severe";
      } else if (ebitdaAtRiskPct >= 0.10) {
        severity = "Elevated";
      }
    }
    
    // Track modifiers
    const modifiers = { noMsa: false, noAssignment: false };
    
    // Escalate severity if contract clauses are missing
    // Only escalate when explicitly false (not null/unknown)
    if (hasMsa === false) {
      modifiers.noMsa = true;
      if (severity === "Moderate") severity = "Elevated";
      else if (severity === "Elevated") severity = "Severe";
    }
    
    if (hasAssignmentClause === false) {
      modifiers.noAssignment = true;
      if (severity === "Moderate") severity = "Elevated";
      else if (severity === "Elevated") severity = "Severe";
    }
    
    return {
      canCompute: true,
      largestCustomerPctRevenue: customerPct,
      ebitdaMarginUsed: ebitdaMargin,
      ebitdaAtRiskDollar,
      ebitdaAtRiskPct,
      severity,
      modifiers
    };
  };

  // Overtime Leakage calculation (PROMPT 6)
  const computeOvertimeLeakage = (params) => {
    const {
      actualOtPct,
      revenue,
      grossMargin,
      ebitda,
      explicitTechnicianPayroll
    } = params;
    
    const BASELINE_OT_PCT = 0.12; // 12% industry baseline
    
    // Check if we have OT %
    if (actualOtPct === null || actualOtPct === undefined) {
      return {
        canCompute: false,
        reasonIfCannotCompute: "Overtime % of payroll not available",
        baselineOtPct: BASELINE_OT_PCT,
        actualOtPct: null,
        excessOtPct: null,
        technicianPayrollUsed: null,
        payrollSource: "Unavailable",
        leakageDollar: null,
        leakagePctOfEbitda: null,
        isMaterial: false
      };
    }
    
    // Normalize OT % to decimal
    const otPct = actualOtPct > 1 ? actualOtPct / 100 : actualOtPct;
    
    // If OT is at or below baseline, no leakage
    if (otPct <= BASELINE_OT_PCT) {
      return {
        canCompute: true,
        reasonIfCannotCompute: null,
        baselineOtPct: BASELINE_OT_PCT,
        actualOtPct: otPct,
        excessOtPct: 0,
        technicianPayrollUsed: null,
        payrollSource: "Not needed",
        leakageDollar: 0,
        leakagePctOfEbitda: null,
        isMaterial: false
      };
    }
    
    const excessOtPct = otPct - BASELINE_OT_PCT;
    
    // Determine technician payroll
    let technicianPayroll = null;
    let payrollSource = "Unavailable";
    
    if (explicitTechnicianPayroll !== null && explicitTechnicianPayroll !== undefined) {
      technicianPayroll = explicitTechnicianPayroll;
      payrollSource = "Explicit";
    } else if (revenue !== null && revenue !== undefined && grossMargin !== null && grossMargin !== undefined) {
      // Estimate payroll = Revenue × (1 - Gross Margin)
      const gm = grossMargin > 1 ? grossMargin / 100 : grossMargin;
      technicianPayroll = revenue * (1 - gm);
      payrollSource = "Estimated";
    }
    
    if (technicianPayroll === null) {
      return {
        canCompute: false,
        reasonIfCannotCompute: "Technician payroll not available (explicit or estimable from Revenue and Gross Margin)",
        baselineOtPct: BASELINE_OT_PCT,
        actualOtPct: otPct,
        excessOtPct,
        technicianPayrollUsed: null,
        payrollSource: "Unavailable",
        leakageDollar: null,
        leakagePctOfEbitda: null,
        isMaterial: false
      };
    }
    
    // Calculate leakage
    const leakageDollar = excessOtPct * technicianPayroll;
    
    // Calculate % of EBITDA
    let leakagePctOfEbitda = null;
    let isMaterial = false;
    
    if (ebitda !== null && ebitda !== undefined && Math.abs(ebitda) > 0) {
      leakagePctOfEbitda = leakageDollar / Math.abs(ebitda);
      isMaterial = leakagePctOfEbitda > 0.03; // > 3% of EBITDA
    }
    
    return {
      canCompute: true,
      reasonIfCannotCompute: null,
      baselineOtPct: BASELINE_OT_PCT,
      actualOtPct: otPct,
      excessOtPct,
      technicianPayrollUsed: technicianPayroll,
      payrollSource,
      leakageDollar,
      leakagePctOfEbitda,
      isMaterial
    };
  };

  // Working Capital Engine calculation (PROMPT 4)
  const computeWorkingCapitalEngine = (params) => {
    const { actualDsoDays, benchmarkDsoDays, annualRevenue, ebitda } = params;
    
    // Default benchmark = 60 days if not provided
    const benchmark = benchmarkDsoDays ?? 60;
    
    // Check if we have required values
    if (actualDsoDays === null || actualDsoDays === undefined || !annualRevenue) {
      return {
        benchmarkDsoDays: benchmark,
        impliedCashRelease: null,
        impliedCashReleasePctOfEbitda: null,
        isMaterial: false,
        classification: "One-time cash release (not recurring EBITDA)",
        notes: ["Insufficient data for calculation"]
      };
    }
    
    // Calculate implied cash release
    const cashRelease = ((actualDsoDays - benchmark) / 365) * annualRevenue;
    
    // If negative or zero, no cash release
    if (cashRelease <= 0) {
      return {
        benchmarkDsoDays: benchmark,
        impliedCashRelease: 0,
        impliedCashReleasePctOfEbitda: null,
        isMaterial: false,
        classification: "One-time cash release (not recurring EBITDA)",
        notes: ["DSO at or below benchmark - no cash release implied"]
      };
    }
    
    // Calculate % of EBITDA
    let pctOfEbitda = null;
    let isMaterial = false;
    
    if (ebitda && Math.abs(ebitda) > 0) {
      pctOfEbitda = cashRelease / Math.abs(ebitda);
      isMaterial = pctOfEbitda > 0.05; // > 5% of EBITDA
    }
    
    return {
      benchmarkDsoDays: benchmark,
      impliedCashRelease: cashRelease,
      impliedCashReleasePctOfEbitda: pctOfEbitda,
      isMaterial,
      classification: "One-time cash release (not recurring EBITDA)",
      notes: isMaterial ? ["Material working capital improvement identified"] : []
    };
  };

  // Render financial data tab with IC memo style
  const renderFinancialData = () => {
    const fin = findings?.financial || {};
    
    console.log('[AIFindings] renderFinancialData - Revenue:', fin.revenue, 'Period:', fin.revenuePeriod);
    
    // Compute Working Capital Engine outputs
    const wcEngine = computeWorkingCapitalEngine({
      actualDsoDays: fin.dso,
      benchmarkDsoDays: findings?.workingCapitalBenchmarkDays,
      annualRevenue: fin.revenue,
      ebitda: fin.ebitda
    });
    
    // Build found/missing metrics for data coverage
    const foundMetrics = [];
    const missingMetrics = [];
    
    // Helper to check if value exists (including 0 as valid)
    const hasValue = (v) => v !== null && v !== undefined && !isNaN(v);
    
    if (hasValue(fin.revenue)) foundMetrics.push({ key: 'revenue', label: 'Revenue', value: fin.revenue });
    else missingMetrics.push('revenue');
    
    if (hasValue(fin.ebitda)) foundMetrics.push({ key: 'ebitda', label: 'EBITDA', value: fin.ebitda });
    else missingMetrics.push('ebitda');
    
    if (hasValue(fin.netDebt)) foundMetrics.push({ key: 'net_debt', label: 'Net Debt', value: fin.netDebt });
    else missingMetrics.push('net_debt');
    
    if (hasValue(fin.revolverAvailability)) foundMetrics.push({ key: 'revolver_availability', label: 'Revolver Availability', value: fin.revolverAvailability });
    else missingMetrics.push('revolver_availability');
    
    if (hasValue(fin.grossMargin)) foundMetrics.push({ key: 'gross_margin', label: 'Gross Margin', value: fin.grossMargin });
    else missingMetrics.push('gross_margin');
    
    if (hasValue(fin.dso)) foundMetrics.push({ key: 'dso', label: 'DSO', value: fin.dso });
    else missingMetrics.push('dso');
    
    if (hasValue(fin.leaseExpense)) foundMetrics.push({ key: 'lease_expense', label: 'Lease Expense', value: fin.leaseExpense });
    else missingMetrics.push('lease_expense');
    
    if (hasValue(fin.insuranceExpense)) foundMetrics.push({ key: 'insurance_expense', label: 'Insurance Expense', value: fin.insuranceExpense });
    else missingMetrics.push('insurance_expense');
    
    if (hasValue(fin.itExpense)) foundMetrics.push({ key: 'it_expense', label: 'IT Expense', value: fin.itExpense });
    else missingMetrics.push('it_expense');
    
    if (hasValue(fin.customerConcentration?.top_1)) foundMetrics.push({ key: 'customer_concentration', label: 'Customer Concentration', value: fin.customerConcentration.top_1 });
    else missingMetrics.push('customer_concentration');
    
    if (hasValue(fin.customerCount)) foundMetrics.push({ key: 'customer_count', label: 'Customer Count', value: fin.customerCount });
    else missingMetrics.push('customer_count');
    
    // EBITDA variants - handle both object with value/period and direct value formats
    const ebitdaVariants = [];
    if (fin.ebitdaVariants) {
      const variantMap = {
        reported: 'Reported',
        management_adjusted: 'Management Adjusted',
        seller_adjusted: 'Seller Adjusted',
        qoe_adjusted: 'QoE Adjusted',
        pro_forma_synergized: 'Pro Forma Synergized'
      };
      
      Object.entries(variantMap).forEach(([key, label]) => {
        const variantData = fin.ebitdaVariants[key];
        if (variantData) {
          // Handle both {value, period} object and direct value
          const value = typeof variantData === 'object' ? variantData.value : variantData;
          const period = typeof variantData === 'object' ? variantData.period : fin.ebitdaPeriod;
          
          if (value !== null && value !== undefined) {
            ebitdaVariants.push({ variant: label, value, period: period || fin.ebitdaPeriod });
          }
        }
      });
    }
    
    // Calculate exposure proxy
    const canCalculateExposure = hasValue(fin.revenue) && hasValue(fin.grossMargin);
    const exposureProxy = canCalculateExposure ? Number(fin.revenue) * Number(fin.grossMargin) : null;
    
    // KPI Card component
    const KpiCard = ({ icon: Icon, label, value, period, metricKey, calculated }) => (
      <div className="kpi-card">
        <div className="kpi-header">
          <Icon size={18} className="kpi-icon" />
          <span className="kpi-label">{label}</span>
        </div>
        <div className="kpi-value">{value || 'Not found'}</div>
        {period && (
          <div className="kpi-period">
            {period}
            {calculated && <span className="calculated-badge"> (Calculated)</span>}
          </div>
        )}
      </div>
    );
    
    // Metric row component
    const MetricRow = ({ label, value, period, unit, calculated }) => (
      <div className="metric-row">
        <div className="metric-info">
          <span className="metric-label">{label}</span>
        </div>
        <div className="metric-values">
          <span className="metric-value">{formatMetric(value, unit)}</span>
          {period && (
            <span className="metric-period">
              {period}
              {calculated && <span className="calculated-badge"> (Calc)</span>}
            </span>
          )}
        </div>
      </div>
    );
    
    return (
      <div className="financial-data-section-ic">
        <h3 className="section-title-ic">Financial Metrics Extract</h3>
        
        {/* A) Top KPI Strip */}
        <div className="kpi-strip">
          <KpiCard 
            icon={TrendingUp} 
            label="Revenue" 
            value={formatCurrency(fin.revenue)}
            period={fin.revenuePeriod}
          />
          <KpiCard 
            icon={DollarSign} 
            label="EBITDA" 
            value={formatCurrency(fin.ebitda)}
            period={fin.ebitdaPeriod}
          />
          <KpiCard 
            icon={Wallet} 
            label="Net Debt" 
            value={formatCurrency(fin.netDebt)}
            period={fin.netDebtPeriod}
          />
          <KpiCard 
            icon={CreditCard} 
            label="Revolver Availability" 
            value={formatCurrency(fin.revolverAvailability)}
            period={fin.revolverPeriod}
          />
        </div>
        
        {/* B) Detailed EBITDA Bridge Card */}
        {findings?.ebitdaBridge && <EbitdaBridgeCard bridge={findings.ebitdaBridge} netDebt={fin.netDebt} revenue={fin.revenue} />}
        
        {/* C) Extracted Financials Block */}
        {(hasValue(fin.grossProfit) || hasValue(fin.grossMargin)) && (
          <div className="financial-card-ic">
            <h4 className="card-title-ic">
              <TrendingUp size={18} />
              Extracted Financials
            </h4>
            <div className="metrics-grid-2col">
              <MetricRow label="Gross Profit" value={fin.grossProfit} period={fin.grossProfitPeriod} unit="currency" />
              <MetricRow label="Gross Margin" value={fin.grossMargin} period={fin.grossMarginPeriod} unit="percent" calculated={fin.grossMarginCalculated} />
            </div>
          </div>
        )}
        
        {/* E) Working Capital and Cash Metrics */}
        <div className="financial-card-ic">
          <h4 className="card-title-ic">
            <Clock size={18} />
            Working Capital & Cash Metrics
          </h4>
          <div className="metrics-grid-2col">
            <MetricRow label="DSO (Days Sales Outstanding)" value={fin.dso} period={fin.dsoPeriod} unit="days" />
            <MetricRow label="Customer Concentration" value={fin.customerConcentration?.top_1} period={fin.customerConcentrationPeriod} unit="percent" />
            <MetricRow label="Lease Expense" value={fin.leaseExpense} period={fin.leasePeriod} unit="currency" />
            <MetricRow label="Insurance Expense" value={fin.insuranceExpense} period={fin.insurancePeriod} unit="currency" />
            <MetricRow label="IT Expense" value={fin.itExpense} period={fin.itPeriod} unit="currency" />
          </div>
          
          {/* Working Capital Engine Sub-section */}
          {wcEngine.impliedCashRelease !== null && (
            <div className="working-capital-engine">
              <h5 className="wce-header">
                <Info size={14} />
                Working Capital Engine
              </h5>
              
              <div className="wce-rows">
                <div className="wce-row">
                  <span className="wce-label">Benchmark DSO</span>
                  <span className="wce-value">
                    {wcEngine.benchmarkDsoDays} days
                    {!findings?.workingCapitalBenchmarkDays && (
                      <span className="wce-sublabel">(default)</span>
                    )}
                  </span>
                </div>
                
                <div className="wce-row">
                  <span className="wce-label">Implied Cash Release</span>
                  <span className="wce-value">
                    {formatCurrency(wcEngine.impliedCashRelease)}
                    <span className="wce-sublabel one-time">one-time cash</span>
                  </span>
                </div>
                
                {wcEngine.impliedCashReleasePctOfEbitda !== null && (
                  <div className="wce-row">
                    <span className="wce-label">Cash Release vs EBITDA</span>
                    <span className="wce-value">
                      {(wcEngine.impliedCashReleasePctOfEbitda * 100).toFixed(1)}% of EBITDA
                    </span>
                  </div>
                )}
              </div>
              
              {wcEngine.isMaterial && (
                <div className="wce-material-flag">
                  <AlertTriangle size={12} />
                  <span>Material (&gt;5% of EBITDA)</span>
                </div>
              )}
              
              <div className="wce-classification">
                <Info size={12} />
                <span>{wcEngine.classification}</span>
              </div>
              
              {wcEngine.notes.length > 0 && (
                <div className="wce-notes">
                  {wcEngine.notes.map((note, idx) => (
                    <span key={idx} className="wce-note">{note}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* F) Revenue Quality - Customer Concentration Shock Model */}
        {(() => {
          // Get clause detection data from findings
          const clauseData = findings?.raw?.detected_clauses || findings?.detectedClauses || {};
          const clauseMap = Array.isArray(clauseData) 
            ? Object.fromEntries(clauseData.map(c => [c.type, c]))
            : clauseData;
          
          // Check for MSA and assignment clause
          const msaClause = clauseMap['master_services_agreement'] || clauseMap['msa'];
          const assignmentClause = clauseMap['assignment'];
          
          const hasMsa = msaClause?.detected === true ? true : 
                         msaClause?.detected === false ? false : null;
          const hasAssignmentClause = assignmentClause?.detected === true ? true :
                                      assignmentClause?.detected === false ? false : null;
          
          // Compute shock model
          const shockModel = computeCustomerConcentrationShock({
            largestCustomerPctRevenue: fin.customerConcentration?.top_1,
            revenue: fin.revenue,
            ebitda: fin.ebitda,
            ebitdaMarginOptional: fin.ebitda && fin.revenue ? fin.ebitda / fin.revenue : null,
            hasMsa,
            hasAssignmentClause
          });
          
          if (!shockModel.canCompute) {
            return (
              <div className="financial-card-ic">
                <h4 className="card-title-ic">
                  <AlertTriangle size={18} />
                  Revenue Quality
                </h4>
                <div className="revenue-quality-section">
                  <div className="shock-model-unavailable">
                    <span className="unavailable-text">Customer Exposure panel unavailable</span>
                    <span className="unavailable-reason">{shockModel.reasonIfCannotCompute}</span>
                  </div>
                </div>
              </div>
            );
          }
          
          return (
            <div className="financial-card-ic">
              <h4 className="card-title-ic">
                <AlertTriangle size={18} />
                Revenue Quality
              </h4>
              <div className="revenue-quality-section">
                <h5 className="rq-subheader">Customer Concentration Shock Model</h5>
                
                <div className="shock-model-rows">
                  <div className="shock-row">
                    <span className="shock-label">Largest customer</span>
                    <span className="shock-value">
                      {(shockModel.largestCustomerPctRevenue * 100).toFixed(1)}% of revenue
                    </span>
                  </div>
                  
                  <div className="shock-row">
                    <span className="shock-label">EBITDA at risk</span>
                    <span className="shock-value">
                      {formatCurrency(shockModel.ebitdaAtRiskDollar)}
                    </span>
                  </div>
                  
                  {shockModel.ebitdaAtRiskPct !== null && (
                    <div className="shock-row">
                      <span className="shock-label">EBITDA at risk</span>
                      <span className="shock-value">
                        {(shockModel.ebitdaAtRiskPct * 100).toFixed(1)}% of EBITDA
                      </span>
                    </div>
                  )}
                </div>
                
                <div className={`shock-severity severity-${shockModel.severity.toLowerCase()}`}>
                  <span className="severity-label">Exposure:</span>
                  <span className="severity-chip">{shockModel.severity}</span>
                </div>
                
                {(shockModel.modifiers.noMsa || shockModel.modifiers.noAssignment) && (
                  <div className="shock-modifiers">
                    {shockModel.modifiers.noMsa && (
                      <span className="modifier-badge warning">No MSA</span>
                    )}
                    {shockModel.modifiers.noAssignment && (
                      <span className="modifier-badge warning">No assignment clause</span>
                    )}
                  </div>
                )}
                
                <div className="shock-disclaimer">
                  <Info size={12} />
                  <span>Exposure is a shock scenario estimate, not a forecast.</span>
                </div>
              </div>
            </div>
          );
        })()}
        
        {/* G) Data Coverage Card */}
        <div className="financial-card-ic">
          <h4 className="card-title-ic">
            <CheckCircle size={18} />
            Data Coverage
          </h4>
          <div className="coverage-stats">
            <div className="coverage-count">
              <span className="coverage-found">{foundMetrics.length}</span>
              <span className="coverage-total">/ {FINANCIAL_METRICS_CONFIG.length} metrics found</span>
            </div>
            <div className="coverage-bar">
              <div 
                className="coverage-fill" 
                style={{ width: `${(foundMetrics.length / FINANCIAL_METRICS_CONFIG.length) * 100}%` }}
              />
            </div>
          </div>
          <div className="coverage-lists">
            <div className="coverage-found-list">
              <h5>Found</h5>
              <ul>
                {foundMetrics.slice(0, 8).map((m, idx) => (
                  <li key={idx}><Check size={14} /> {m.label}</li>
                ))}
                {foundMetrics.length > 8 && !expandedCoverage.found && (
                  <li 
                    className="more-items-button"
                    onClick={() => setExpandedCoverage(prev => ({ ...prev, found: true }))}
                  >
                    +{foundMetrics.length - 8} more
                  </li>
                )}
                {expandedCoverage.found && foundMetrics.slice(8).map((m, idx) => (
                  <li key={`extra-${idx}`}><Check size={14} /> {m.label}</li>
                ))}
              </ul>
            </div>
            <div className="coverage-missing-list">
              <h5>Not Found</h5>
              <ul>
                {missingMetrics.slice(0, 5).map((key, idx) => {
                  const config = FINANCIAL_METRICS_CONFIG.find(c => c.key === key);
                  return <li key={idx}><Circle size={14} /> {config?.label || key}</li>;
                })}
                {missingMetrics.length > 5 && !expandedCoverage.missing && (
                  <li 
                    className="more-items-button"
                    onClick={() => setExpandedCoverage(prev => ({ ...prev, missing: true }))}
                  >
                    +{missingMetrics.length - 5} more
                  </li>
                )}
                {expandedCoverage.missing && missingMetrics.slice(5).map((key, idx) => {
                  const config = FINANCIAL_METRICS_CONFIG.find(c => c.key === key);
                  return <li key={`extra-missing-${idx}`}><Circle size={14} /> {config?.label || key}</li>;
                })}
              </ul>
            </div>
          </div>
        </div>
        
        {/* Exposure Proxy Formula */}
        <div className="financial-card-ic exposure-card">
          <h4 className="card-title-ic">
            <Percent size={18} />
            Financial Exposure Analysis
          </h4>
          <div className="exposure-formula-ic">
            <span className="formula-label">Exposure Proxy = Revenue × Gross Margin</span>
          </div>
          {canCalculateExposure ? (
            <div className="exposure-result-ic calculated">
              <span className="exposure-value-ic">{formatCurrency(exposureProxy)}</span>
              <span className="exposure-status-ic">Calculated from extracted data</span>
            </div>
          ) : hasValue(fin.revenue) ? (
            <div className="exposure-result-ic partial">
              <span className="exposure-value-ic">Partial Data</span>
              <span className="exposure-status-ic">Revenue available but gross margin not found</span>
            </div>
          ) : (
            <div className="exposure-result-ic unknown">
              <span className="exposure-value-ic">Not Available</span>
              <span className="exposure-status-ic">Insufficient financial data for exposure calculation</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render clause detection tab
  const renderClauseDetection = () => {
    // Debug logging
    console.log('Clause raw', findings.detectedClauses);
    
    // Map clause results by type for easy lookup
    const clauseMap = {};
    findings.detectedClauses?.forEach(c => {
      clauseMap[c.type] = c;
    });
    
    // Calculate coverage stats
    const totalClauses = CLAUSE_CATEGORIES.length;
    const detectedCount = CLAUSE_CATEGORIES.filter(c => clauseMap[c.id]?.detected).length;
    
    return (
      <div className="clause-detection-section">
        <h3>Clause Detection Analysis</h3>
        <p className="section-description">
          AI-powered detection of key contractual clauses per technical specification
        </p>
        
        <div className="clause-coverage-line">
          Detected: {detectedCount} of {totalClauses}
        </div>
        
        <div className="clause-categories-grid">
          {CLAUSE_CATEGORIES.map(clause => {
            const clauseResult = clauseMap[clause.id];
            const isDetected = clauseResult?.detected || false;
            const IconComponent = clause.icon;
            
            return (
              <div key={clause.id} className={`clause-card ${isDetected ? 'detected' : 'not-detected'}`}>
                <div className="clause-header">
                  <span className="clause-icon"><IconComponent size={16} /></span>
                  <div className="clause-title-wrapper">
                    <span className="clause-title">{clause.label}</span>
                    <span className="clause-description">{clause.description}</span>
                  </div>
                  {isDetected ? (
                    <span className="clause-badge detected">Detected</span>
                  ) : (
                    <span className="clause-badge not-detected">Not detected</span>
                  )}
                </div>
                
                {isDetected && clauseResult.snippets && clauseResult.snippets.map((snippet, idx) => (
                  <div key={idx} className="clause-instance">
                    <div className="clause-risk-indicator">
                      <span className={`risk-dot ${(clauseResult.riskIndication || clauseResult.risk || 'neutral').toLowerCase()}`}></span>
                      <span className="clause-location">
                        {snippet.page ? `Page ${snippet.page}` : ''}
                        {snippet.section ? `, ${snippet.section}` : ''}
                      </span>
                    </div>
                    <p className="clause-text">"{snippet.text?.substring(0, 150) || clauseResult.text?.substring(0, 150) || 'No text available'}..."</p>
                    <div className="clause-meta">
                      <span className="confidence-tag">Confidence: {Math.round((clauseResult.confidence || 0) * 100)}%</span>
                      {(clauseResult.riskIndication || clauseResult.risk) && (clauseResult.riskIndication || clauseResult.risk) !== 'neutral' && (
                        <span className={`risk-tag ${(clauseResult.riskIndication || clauseResult.risk || '').toLowerCase()}`}>{clauseResult.riskIndication || clauseResult.risk}</span>
                      )}
                    </div>
                  </div>
                ))}
                {isDetected && canEdit && analysisMode === 'deal' && (
                  <button
                    className={`clause-add-note-btn${clauseNoteAdded[clause.id] ? ' added' : ''}`}
                    onClick={() => addClauseToNotes(clauseResult, clause.label)}
                    disabled={addingNoteForClause === clause.id || clauseNoteAdded[clause.id]}
                  >
                    {clauseNoteAdded[clause.id] ? '✓ Added to Notes' : addingNoteForClause === clause.id ? 'Adding...' : '+ Add to Finding Notes'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render operational data tab
  const renderOperationalData = () => {
    const op = findings?.operational || {};
    
    // Helper to format currency
    const fmtCurrency = (val) => {
      if (val === null || val === undefined || isNaN(val)) return null;
      const num = Number(val);
      if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
      if (num >= 1000) return `$${(num / 1000).toFixed(0)}k`;
      return `$${num.toFixed(0)}`;
    };
    
    // Calculate coverage stats - ensure total is actually a number
    const headcountTotal = op.headcount?.total;
    const hasHeadcount = typeof headcountTotal === 'number' && headcountTotal > 0;
    const hasLocations = op.offices?.length > 0;
    const hasSaaS = op.saasStack?.length > 0;
    const hasVendors = op.vendors?.length > 0;
    
    const coverageFound = [hasHeadcount, hasLocations, hasSaaS, hasVendors].filter(Boolean).length;
    
    // Use coverageFound to avoid unused variable warning
    const coveragePercent = Math.round((coverageFound / 4) * 100);
    
    return (
      <div className="operational-data-section">
        <h3>Operational Data Extraction</h3>
        <p className="section-description">
          Vendor relationships, SaaS stack, and organizational structure
        </p>
        
        {/* Coverage line */}
        <div className="operational-coverage">
          Coverage: Headcount {hasHeadcount ? 'found' : 'missing'} • Locations {hasLocations ? 'found' : 'missing'} • SaaS {hasSaaS ? 'found' : 'missing'} • Vendors {hasVendors ? 'found' : 'missing'}
        </div>
        
        <div className="operational-grid">
          {/* Headcount Card */}
          <div className="operational-card">
            <h4>
              Headcount
              {op.headcount.hasAmbiguity && (
                <span 
                  className="ambiguity-indicator" 
                  title={`Multiple totals found: ${op.headcount.candidates.map(c => c.value).join(', ')}`}
                  style={{ marginLeft: '6px', cursor: 'help' }}
                >
                  <Info size={14} style={{ opacity: 0.6 }} />
                </span>
              )}
            </h4>
            {hasHeadcount ? (
              <>
                <div className="operational-metric">
                  <span className="metric-value">
                    {op.headcount.total?.toLocaleString() || 'N/A'}
                  </span>
                  <span className="metric-label">
                    Total Employees
                    {op.headcount.warning && (
                      <span className="headcount-warning" style={{ display: 'block', fontSize: '11px', color: '#f59e0b', marginTop: '4px' }}>
                        ⚠ {op.headcount.warning}
                      </span>
                    )}
                  </span>
                </div>
                {op.headcount.byFunction?.length > 0 && (
                  <div className="function-breakdown">
                    {(showAllFunctions ? op.headcount.byFunction : op.headcount.byFunction.slice(0, 3)).map((item, idx) => (
                      <div key={idx} className="function-item">
                        <span className="function-name">{item.function || item.label || 'Unknown'}</span>
                        <span className="function-count">{item.count || item.value || 0}</span>
                      </div>
                    ))}
                    {op.headcount.byFunction.length > 3 && (
                      <button 
                        className="function-item view-all-btn"
                        onClick={() => setShowAllFunctions(!showAllFunctions)}
                      >
                        <span className="function-name">
                          {showAllFunctions ? 'Show less' : `+${op.headcount.byFunction.length - 3} more`}
                        </span>
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="no-data">No headcount data found in documents</p>
            )}
          </div>
          
          {/* Office Locations Card */}
          <div className="operational-card">
            <h4>Office Locations</h4>
            {hasLocations ? (
              <ul className="location-list">
                {op.offices.slice(0, 5).map((loc, idx) => (
                  <li key={idx}>
                    <span className="location-name">
                      {loc.metro || loc.city || 'Unknown'}
                      {loc.state ? `, ${loc.state}` : ''}
                      {loc.country && loc.country !== 'USA' ? ` (${loc.country})` : ''}
                    </span>
                    {loc.annual_lease_cost !== null && loc.annual_lease_cost !== undefined && (
                      <span className="location-cost">{fmtCurrency(loc.annual_lease_cost)}</span>
                    )}
                  </li>
                ))}
                {op.offices.length > 5 && (
                  <li className="view-all">View all {op.offices.length} locations</li>
                )}
              </ul>
            ) : (
              <p className="no-data">No office locations detected</p>
            )}
          </div>
          
          {/* SaaS Stack Card */}
          <div className="operational-card">
            <h4>SaaS Stack</h4>
            {hasSaaS ? (
              <ul className="saas-detail-list">
                {op.saasStack.slice(0, 8).map((saas, idx) => (
                  <li key={idx}>
                    <span className="saas-name">{saas.name || saas || 'Unknown'}</span>
                    {saas.category && <span className="saas-category">{saas.category}</span>}
                    {saas.annual_cost !== null && saas.annual_cost !== undefined && (
                      <span className="saas-cost">{fmtCurrency(saas.annual_cost)}</span>
                    )}
                  </li>
                ))}
                {op.saasStack.length > 8 && (
                  <li className="view-all">View all {op.saasStack.length} tools</li>
                )}
              </ul>
            ) : (
              <p className="no-data">No SaaS tools detected</p>
            )}
          </div>
          
          {/* Top Vendors Card */}
          <div className="operational-card">
            <h4>Top Vendors</h4>
            {hasVendors ? (
              <ul className="vendor-detail-list">
                {op.vendors
                  .sort((a, b) => (b.annual_spend || 0) - (a.annual_spend || 0))
                  .slice(0, 8)
                  .map((vendor, idx) => (
                    <li key={idx}>
                      <span className="vendor-name">{vendor.name || vendor || 'Unknown'}</span>
                      {vendor.annual_spend !== null && vendor.annual_spend !== undefined && (
                        <span className="vendor-spend">{fmtCurrency(vendor.annual_spend)}</span>
                      )}
                    </li>
                  ))}
                {op.vendors.length > 8 && (
                  <li className="view-all">View all {op.vendors.length} vendors</li>
                )}
              </ul>
            ) : (
              <p className="no-data">No vendors detected</p>
            )}
          </div>
          
          {/* Operational Leakage: Overtime Efficiency Card (PROMPT 6) */}
          {(() => {
            const fin = findings?.financial || {};
            const otLeakage = computeOvertimeLeakage({
              actualOtPct: fin.overtimePctPayroll,
              revenue: fin.revenue,
              grossMargin: fin.grossMargin,
              ebitda: fin.ebitda,
              explicitTechnicianPayroll: null // Not explicitly extracted yet
            });
            
            return (
              <div className="operational-card operational-leakage-card">
                <h4>Operational Leakage</h4>
                <p className="leakage-subtitle">Overtime Efficiency</p>
                
                {!otLeakage.canCompute ? (
                  <div className="leakage-unavailable">
                    <p className="unavailable-title">Insufficient data to estimate overtime leakage</p>
                    <p className="unavailable-reason">{otLeakage.reasonIfCannotCompute}</p>
                    {otLeakage.actualOtPct !== null && (
                      <div className="leakage-row">
                        <span className="leakage-label">Overtime %</span>
                        <span className="leakage-value">{(otLeakage.actualOtPct * 100).toFixed(1)}%</span>
                      </div>
                    )}
                    <div className="leakage-row">
                      <span className="leakage-label">Baseline</span>
                      <span className="leakage-value">{(otLeakage.baselineOtPct * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="leakage-rows">
                      <div className="leakage-row">
                        <span className="leakage-label">Overtime %</span>
                        <span className="leakage-value">{(otLeakage.actualOtPct * 100).toFixed(1)}%</span>
                      </div>
                      <div className="leakage-row">
                        <span className="leakage-label">Baseline</span>
                        <span className="leakage-value baseline">{(otLeakage.baselineOtPct * 100).toFixed(0)}%</span>
                      </div>
                      {otLeakage.excessOtPct > 0 && (
                        <div className="leakage-row">
                          <span className="leakage-label">Excess overtime</span>
                          <span className="leakage-value excess">+{(otLeakage.excessOtPct * 100).toFixed(1)}%</span>
                        </div>
                      )}
                      {otLeakage.leakageDollar > 0 && (
                        <div className="leakage-row">
                          <span className="leakage-label">Implied leakage (annual)</span>
                          <span className="leakage-value">
                            {formatCurrency(otLeakage.leakageDollar)}
                            <span className="leakage-sublabel">run-rate leakage</span>
                          </span>
                        </div>
                      )}
                      {otLeakage.leakagePctOfEbitda !== null && (
                        <div className="leakage-row">
                          <span className="leakage-label">Leakage vs EBITDA</span>
                          <span className="leakage-value">{(otLeakage.leakagePctOfEbitda * 100).toFixed(1)}% of EBITDA</span>
                        </div>
                      )}
                    </div>
                    
                    {otLeakage.isMaterial && (
                      <div className="leakage-material-flag">
                        <AlertTriangle size={12} />
                        <span>Material (&gt;3% of EBITDA)</span>
                      </div>
                    )}
                    
                    <div className="leakage-assumptions">
                      <span className="assumption-item">Baseline OT = 12%</span>
                      <span className="assumption-item">Payroll source: {otLeakage.payrollSource}</span>
                    </div>
                    
                    <div className="leakage-classification">
                      <span className="classification-chip">Operational efficiency synergy</span>
                      
                      <div className="classification-toggle">
                        <label className="toggle-label">Classification:</label>
                        <div className="toggle-options">
                          <button
                            className={`toggle-btn ${otClassification === 'standalone' ? 'active' : ''}`}
                            onClick={() => setOtClassification('standalone')}
                          >
                            Standalone
                          </button>
                          <button
                            className={`toggle-btn ${otClassification === 'integration' ? 'active' : ''}`}
                            onClick={() => setOtClassification('integration')}
                          >
                            Integration-enabled
                          </button>
                        </div>
                      </div>
                      
                      <p className="toggle-explanation">
                        {otClassification === 'standalone' 
                          ? 'Improvement achievable without integration.' 
                          : 'Requires integration actions post-close.'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          
          {/* Real Estate / Leases Card (Prompt 8) */}
          <div className="operational-card lease-card">
            <h4>Real Estate / Leases</h4>
            {op.leases && op.leases.length > 0 ? (
              <div className="leases-list">
                {op.leases.map((lease, idx) => (
                  <div key={idx} className="lease-item">
                    <div className="lease-header">
                      <span className="lease-metro">{lease.metro || 'Unknown Location'}</span>
                      <span className="lease-rent">
                        {lease.annualRent 
                          ? formatCurrency(lease.annualRent) + '/year'
                          : lease.monthlyRent 
                            ? formatCurrency(lease.monthlyRent) + '/month'
                            : 'Rent not disclosed'
                        }
                      </span>
                    </div>
                    {lease.address && (
                      <span className="lease-address">{lease.address}</span>
                    )}
                    <div className="lease-details">
                      <span className="lease-term">
                        {lease.termRemainingMonths 
                          ? `${lease.termRemainingMonths} months remaining`
                          : 'Term unknown'
                        }
                      </span>
                      <span className="lease-penalty">
                        {lease.terminationPenalty !== null && lease.terminationPenalty !== undefined
                          ? `Penalty: ${formatCurrency(lease.terminationPenalty)}`
                          : 'Penalty: Not disclosed'
                        }
                      </span>
                    </div>
                    {lease.evidenceRefs && lease.evidenceRefs.length > 0 && (
                      <div className="lease-evidence">
                        {lease.evidenceRefs.slice(0, 2).map((ref, ridx) => (
                          <span key={ridx} className="evidence-snippet">
                            {ref.snippet || ref.docName}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">No lease detail detected in documents.</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const riskBadge = getRiskBadge();
  const hasAnalysis = findings && (findings.ddReport != null || findings.detectedClauses?.length > 0);

  // Transform findings to RiskOverview format
  const getRiskOverviewData = () => {
    if (!findings) return null;
    
    const targetName = getTargetName();
    
    // Convert string arrays to RiskItem format
    // Helper to infer tags from risk content
    const inferTags = (risk, bucket) => {
      if (typeof risk === 'object' && risk.tags && Array.isArray(risk.tags)) {
        return risk.tags; // Use AI-generated tags if available
      }
      
      const text = (typeof risk === 'string' ? risk : risk.title || '').toLowerCase();
      const tags = [];
      
      if (text.includes('customer') || text.includes('concentration') || text.includes('msa')) {
        tags.push('CUSTOMER_CONCENTRATION', 'CONTRACT_RISK');
      }
      if (text.includes('contract') || text.includes('legal') || text.includes('agreement')) {
        tags.push('LEGAL', 'CONTRACT_RISK');
      }
      if (text.includes('financial') || text.includes('unaudited') || text.includes('accounting') || text.includes('allocation')) {
        tags.push('FINANCIAL_REPORTING', 'ACCOUNTING_CONTROLS');
      }
      if (text.includes('erp') || text.includes('system') || text.includes('it') || text.includes('technology')) {
        tags.push('IT_SYSTEMS', 'OPERATIONAL');
      }
      if (text.includes('1099') || text.includes('labor') || text.includes('classification') || text.includes('employee')) {
        tags.push('LABOR_COMPLIANCE', 'REGULATORY');
      }
      if (text.includes('weather') || text.includes('volatil') || text.includes('seasonal')) {
        tags.push('REVENUE_VOLATILITY', 'CYCLICAL_RISK');
      }
      if (text.includes('margin') || text.includes('cost') || text.includes('expense')) {
        tags.push('MARGIN_PRESSURE', 'COST_STRUCTURE');
      }
      if (text.includes('cash') || text.includes('liquidity') || text.includes('working capital')) {
        tags.push('LIQUIDITY', 'WORKING_CAPITAL');
      }
      if (text.includes('debt') || text.includes('leverage') || text.includes('covenant')) {
        tags.push('LEVERAGE', 'REFINANCING_RISK');
      }
      if (text.includes('revenue') && !text.includes('volatil')) {
        tags.push('REVENUE_QUALITY');
      }
      
      if (tags.length === 0) {
        tags.push(bucket === 'red' ? 'FINANCIAL' : bucket === 'orange' ? 'OPERATIONAL' : 'COMPLIANCE');
      }
      
      return tags;
    };

    // Helper to generate contextual summary from risk title
    const generateSummary = (risk, bucket) => {
      // Handle string format (backward compatibility)
      if (typeof risk === 'string') {
        const text = risk.toLowerCase();
        // Generate contextual summary based on title keywords
        if (text.includes('erp') || text.includes('system')) return 'Legacy system implementation creates operational drag with ongoing abandonment costs threatening margin structure.';
        if (text.includes('lease') || text.includes('capitalization') || text.includes('ebitda')) return 'Accounting treatment creates earnings volatility with potential QoE adjustments impacting pro forma EBITDA.';
        if (text.includes('payroll') || text.includes('severance') || text.includes('workforce')) return 'Personnel-related restructuring creates one-time costs and potential labor compliance exposure.';
        if (text.includes('facility') || text.includes('consolidation')) return 'Real estate footprint presents both synergy opportunity and integration complexity with unrealized cost savings.';
        if (text.includes('customer') || text.includes('contract')) return 'Customer concentration and contractual provisions introduce change-of-control risk and revenue reliability concerns.';
        return 'Material risk factor affecting investment thesis and requiring structural mitigation.';
      }
      
      // Use detail/description if available and meaningful
      if (risk.detail && risk.detail.length > 20 && risk.detail !== risk.title) {
        return risk.detail;
      }
      
      const text = (risk.title || '').toLowerCase();
      
      // Generate contextual summary based on risk type
      if (text.includes('erp') || text.includes('system') || text.includes('technology')) {
        return 'Legacy system implementation creates operational drag with ongoing abandonment costs threatening margin structure.';
      }
      if (text.includes('lease') || text.includes('capitalization') || text.includes('ebitda') || text.includes('reclass')) {
        return 'Accounting treatment creates earnings volatility with potential QoE adjustments impacting pro forma EBITDA.';
      }
      if (text.includes('payroll') || text.includes('severance') || text.includes('headcount') || text.includes('workforce')) {
        return 'Personnel-related restructuring creates one-time costs and potential labor compliance exposure.';
      }
      if (text.includes('facility') || text.includes('consolidation') || text.includes('overlap') || text.includes('savings')) {
        return 'Real estate footprint presents both synergy opportunity and integration complexity with unrealized cost savings.';
      }
      if (text.includes('timing') || text.includes('variance') || text.includes('renewal')) {
        return 'Contract timing inconsistencies create working capital pressure and revenue recognition challenges.';
      }
      if (text.includes('overtime') || text.includes('dispatch') || text.includes('technician')) {
        return 'Operational inefficiencies and resource constraints drive elevated labor costs and service delivery risk.';
      }
      if (text.includes('insurance') || text.includes('captive') || text.includes('premium')) {
        return 'Captive insurance structure offers cost reduction opportunity through pooled risk and claims management.';
      }
      if (text.includes('synergy') || text.includes('model') || text.includes('tracking')) {
        return 'Proactive synergy tracking demonstrates management sophistication with clear path to operational improvements.';
      }
      if (text.includes('customer') || text.includes('contract') || text.includes('msa') || text.includes('mfn')) {
        return 'Customer concentration and contractual provisions introduce change-of-control risk and revenue reliability concerns.';
      }
      if (text.includes('fleet') || text.includes('vehicle') || text.includes('equipment')) {
        return 'Fleet management practices create accounting complexity with potential normalization adjustments.';
      }
      
      return bucket === 'red' ? 'Critical risk factor requiring immediate attention and structural mitigation.' :
             bucket === 'orange' ? 'Elevated risk consideration warranting protective covenants and monitoring.' :
             bucket === 'low' ? 'Moderate risk factor manageable through standard due diligence.' :
             'Positive indicator supporting investment thesis.';
    };

    const items = [
      ...(findings.highRisks || []).map((risk, idx) => ({
        id: `high-${idx}`,
        bucket: 'red',
        severity: 3,
        title: typeof risk === 'string' ? risk : risk.title || 'Risk',
        summary: generateSummary(risk, 'red'),
        detail: typeof risk === 'string' ? risk : risk.detail || '',
        tags: inferTags(risk, 'red'),
        exposure: { label: 'Exposure: 3x', value: 3 },
        documentName: typeof risk === 'object' && risk.sourceDocument ? risk.sourceDocument : targetName
      })),
      ...(findings.mediumRisks || []).map((risk, idx) => ({
        id: `medium-${idx}`,
        bucket: 'orange',
        severity: 2,
        title: typeof risk === 'string' ? risk : risk.title || 'Risk',
        summary: generateSummary(risk, 'orange'),
        detail: typeof risk === 'string' ? risk : risk.detail || '',
        tags: inferTags(risk, 'orange'),
        exposure: { label: 'Exposure: 2x', value: 2 },
        documentName: typeof risk === 'object' && risk.sourceDocument ? risk.sourceDocument : targetName
      })),
      ...(findings.lowRisks || []).map((risk, idx) => ({
        id: `low-${idx}`,
        bucket: 'low',
        severity: 1,
        title: typeof risk === 'string' ? risk : risk.title || 'Risk',
        summary: generateSummary(risk, 'low'),
        detail: typeof risk === 'string' ? risk : risk.detail || '',
        tags: inferTags(risk, 'low'),
        documentName: typeof risk === 'object' && risk.sourceDocument ? risk.sourceDocument : targetName
      })),
      ...(findings.greenFlags || []).map((flag, idx) => ({
        id: `green-${idx}`,
        bucket: 'green',
        severity: 0,
        title: typeof flag === 'string' ? flag : (flag.title || 'Positive Finding'),
        summary: typeof flag === 'string' ? flag : generateSummary(flag, 'green'),
        detail: typeof flag === 'string' ? flag : flag.detail || '',
        tags: typeof flag === 'object' && flag.tags ? flag.tags : ['STRATEGIC'],
        documentName: typeof flag === 'object' && flag.sourceDocument ? flag.sourceDocument : targetName
      }))
    ];

    // Use AI-generated material drivers if available, otherwise create from high risks
    const materialDrivers = findings.material_drivers || findings.materialDrivers || (findings.highRisks || []).slice(0, 5).map((risk, idx) => {
      // Infer tags from risk title/content
      const riskText = (typeof risk === 'string' ? risk : risk.title || '').toLowerCase();
      const riskDetail = typeof risk === 'string' ? '' : (risk.detail || '');
      const tags = [];
      
      if (riskText.includes('customer') || riskText.includes('concentration') || riskText.includes('msa') || riskText.includes('contract')) {
        tags.push('CUSTOMER_CONCENTRATION', 'CONTRACT_RISK');
      }
      if (riskText.includes('financial') || riskText.includes('unaudited') || riskText.includes('accounting') || riskText.includes('capitalization') || riskText.includes('qoe')) {
        tags.push('FINANCIAL_REPORTING', 'AUDIT_QUALITY');
      }
      if (riskText.includes('erp') || riskText.includes('system') || riskText.includes('it') || riskText.includes('technology')) {
        tags.push('IT_SYSTEMS', 'OPERATIONAL');
      }
      if (riskText.includes('1099') || riskText.includes('labor') || riskText.includes('classification') || riskText.includes('workforce') || riskText.includes('compensation') || riskText.includes('wage')) {
        tags.push('LABOR_COMPLIANCE', 'REGULATORY');
      }
      if (riskText.includes('weather') || riskText.includes('volatil') || riskText.includes('demand') || riskText.includes('dispatch') || riskText.includes('overtime')) {
        tags.push('REVENUE_VOLATILITY', 'OPERATIONAL');
      }
      if (riskText.includes('facilit') || riskText.includes('consolidation') || riskText.includes('overlap')) {
        tags.push('SCALABILITY_RISK', 'COST_STRUCTURE');
      }
      if (riskText.includes('mfn') || riskText.includes('change of control') || riskText.includes('assignment')) {
        tags.push('CONTRACT_RISK', 'LEGAL');
      }
      if (tags.length === 0) {
        tags.push('FINANCIAL');
      }
      
      // Generate meaningful narrative based on risk type
      let narrative = riskDetail;
      if (!narrative || narrative.length < 10) {
        if (riskText.includes('erp') || riskText.includes('system')) {
          narrative = 'Legacy system deficiencies and implementation costs threaten operational scalability and margin compression.';
        } else if (riskText.includes('capitalization') || riskText.includes('qoe') || riskText.includes('financial')) {
          narrative = 'Informal accounting practices and lack of audited financials create earnings quality uncertainty and QoE adjustment risk.';
        } else if (riskText.includes('customer') || riskText.includes('contract') || riskText.includes('msa')) {
          narrative = 'Customer concentration and contractual provisions introduce revenue reliability risk and change-of-control exposure.';
        } else if (riskText.includes('labor') || riskText.includes('workforce') || riskText.includes('1099')) {
          narrative = 'Worker classification and compensation structure present regulatory compliance risk and contingent liability exposure.';
        } else if (riskText.includes('facility') || riskText.includes('consolidation')) {
          narrative = 'Overlapping facility footprint and unaddressed consolidation create cost inefficiencies and integration complexity.';
        } else if (riskText.includes('overtime') || riskText.includes('dispatch')) {
          narrative = 'Operational inefficiencies and resource constraints limit scalability and compress operating margins.';
        } else {
          narrative = 'Material risk factor affecting investment thesis and requiring structural mitigation in deal documentation.';
        }
      }
      
      return {
        rank: (idx + 1),
        title: typeof risk === 'string' ? risk : risk.title || 'Risk',
        narrative: narrative,
        valuationImpact: idx === 0 ? '-0.5x to -1.0x' : undefined,
        tags: tags
      };
    });

    // Use AI-generated category scores if available, otherwise create synthetic ones
    const fixedCategoryOrder = [
      'FINANCIAL_PERFORMANCE',
      'REVENUE_QUALITY', 
      'LIQUIDITY_SOLVENCY',
      'LEGAL_COMPLIANCE',
      'OPERATIONAL_SCALABILITY',
      'STRATEGIC_FIT'
    ];
    
    const aiCategoryScores = findings.category_scores || findings.categoryScores || [];
    
    const categoryScores = fixedCategoryOrder.map(key => {
      // Find AI-generated score for this category
      const aiScore = aiCategoryScores.find(c => c.key === key);
      if (aiScore) {
        return {
          key: aiScore.key,
          label: aiScore.label,
          score: aiScore.score,
          severity: aiScore.severity,
          commentary: aiScore.commentary || 'Analysis completed'
        };
      }
      
      // Fall back to synthetic scores with contextual commentary based on risk level
      const baseScore = findings.riskScore || 0;
      const labelMap = {
        'FINANCIAL_PERFORMANCE': 'Financial Performance',
        'REVENUE_QUALITY': 'Revenue Quality',
        'LIQUIDITY_SOLVENCY': 'Liquidity & Solvency',
        'LEGAL_COMPLIANCE': 'Legal & Compliance',
        'OPERATIONAL_SCALABILITY': 'Operational Scalability',
        'STRATEGIC_FIT': 'Strategic Fit'
      };
      
      const multipliers = {
        'FINANCIAL_PERFORMANCE': 1.0,
        'REVENUE_QUALITY': 0.9,
        'LIQUIDITY_SOLVENCY': 0.8,
        'LEGAL_COMPLIANCE': 0.6,
        'OPERATIONAL_SCALABILITY': 0.7,
        'STRATEGIC_FIT': 0.5
      };
      
      const score = Math.round(baseScore * multipliers[key]);
      const severity = score > 70 ? 3 : score > 40 ? 2 : score > 20 ? 1 : 0;
      
      // Generate contextual fallback commentary based on score and category
      let commentary = '';
      if (key === 'FINANCIAL_PERFORMANCE') {
        commentary = score > 70 ? 'Material earnings quality concerns with potential QoE adjustments required.' : 
                     score > 40 ? 'Moderate financial performance risks warranting closer diligence.' :
                     'Financial metrics support investment thesis with standard protections.';
      } else if (key === 'REVENUE_QUALITY') {
        commentary = score > 70 ? 'Customer concentration and contract terms present material revenue reliability risk.' :
                     score > 40 ? 'Some revenue concentration concerns requiring contract review.' :
                     'Diversified revenue base with strong recurring components.';
      } else if (key === 'LIQUIDITY_SOLVENCY') {
        commentary = score > 70 ? 'Working capital constraints and debt servicing pressure threaten near-term liquidity.' :
                     score > 40 ? 'Moderate leverage levels manageable through standard covenants.' :
                     'Strong cash position and balance sheet flexibility.';
      } else if (key === 'LEGAL_COMPLIANCE') {
        commentary = score > 70 ? 'Significant litigation or regulatory exposure requiring legal review.' :
                     score > 40 ? 'Standard contractual risks manageable through reps and warranties.' :
                     'Clean legal profile with minimal contingent liability exposure.';
      } else if (key === 'OPERATIONAL_SCALABILITY') {
        commentary = score > 70 ? 'IT systems and capacity constraints limit growth scalability.' :
                     score > 40 ? 'Operational improvements needed but manageable post-close.' :
                     'Well-developed infrastructure supports scaling without material investment.';
      } else {
        commentary = score > 70 ? 'Limited strategic fit or synergy realization challenges.' :
                     score > 40 ? 'Moderate strategic alignment with achievable synergies.' :
                     'Strong strategic fit with clear path to value creation.';
      }
      
      return {
        key: key,
        label: labelMap[key],
        score: score,
        severity: severity,
        commentary: commentary
      };
    });

    const confidencePct = Math.round((findings.confidenceScore || 0) * 100);
    
    // Generate executive summary from findings - synthesized narrative
    const generateExecSummary = () => {
      const score = findings.riskScore || 0;
      const label = findings.riskLevel === 'high' || findings.riskLevel === 'critical' ? 'HIGH RISK' : 
                   findings.riskLevel === 'moderate' || findings.riskLevel === 'medium' ? 'STRUCTURED' :
                   findings.riskLevel === 'low' ? 'LOW RISK' : 'ACCEPTABLE';
      
      let summary = `${label} (${score}/100). `;
      
      // Synthesize based on available risk data, not list titles
      const hasFinancialRisks = findings.highRisks?.some(r => 
        typeof r === 'string' ? r.toLowerCase().includes('revenue') || r.toLowerCase().includes('financial') || r.toLowerCase().includes('cash') || r.toLowerCase().includes('loss')
        : (r.title || '').toLowerCase().includes('revenue') || (r.title || '').toLowerCase().includes('financial')
      );
      
      const hasOperationalRisks = findings.highRisks?.some(r =>
        typeof r === 'string' ? r.toLowerCase().includes('operational') || r.toLowerCase().includes('erp') || r.toLowerCase().includes('fleet') || r.toLowerCase().includes('system')
        : (r.title || '').toLowerCase().includes('operational')
      );
      
      const hasLegalRisks = findings.highRisks?.some(r =>
        typeof r === 'string' ? r.toLowerCase().includes('legal') || r.toLowerCase().includes('compliance') || r.toLowerCase().includes('contract')
        : (r.title || '').toLowerCase().includes('legal')
      );
      
      // Build narrative synthesis
      if (findings.highRisks?.length > 0) {
        if (hasFinancialRisks && hasOperationalRisks) {
          summary += "The target exhibits material financial stress compounded by operational inefficiencies, indicating systemic performance deterioration that threatens sustainable cash generation. ";
        } else if (hasFinancialRisks) {
          summary += "The target exhibits material financial stress with liquidity constraints and earnings quality concerns that undermine investment thesis assumptions. ";
        } else if (hasOperationalRisks) {
          summary += "The target presents significant operational scalability and systems integration risks that threaten post-close execution and synergy realization. ";
        } else if (hasLegalRisks) {
          summary += "The target carries material legal and compliance exposures presenting contingent liability risks and potential operational disruption. ";
        } else {
          summary += "The target presents material risk factors across multiple dimensions requiring immediate attention and structural mitigation. ";
        }
      } else if (findings.mediumRisks?.length > 0) {
        summary += "The target exhibits moderate risk considerations across operational and financial dimensions that warrant closer diligence. ";
      } else {
        summary += "The target demonstrates favorable risk characteristics with limited material exposures. ";
      }
      
      // Structural implications
      if (score >= 80) {
        summary += "Structural implications indicate severe liquidity pressure, earnings volatility, and governance deficiencies requiring comprehensive protection packages. ";
      } else if (score >= 65) {
        summary += "Structural considerations around working capital, customer concentration, and margin sustainability warrant protective covenants and price protection mechanisms. ";
      } else if (score >= 50) {
        summary += "Moderate structural considerations exist around operational scalability but remain manageable through standard contractual protections. ";
      } else {
        summary += "The structural profile supports conventional transaction protections without extraordinary measures. ";
      }
      
      // Recommendation
      if (score >= 80) {
        summary += "Proceed only with significant structural protection including price adjustment, escrow, and comprehensive confirmatory diligence prior to LOI.";
      } else if (score >= 65) {
        summary += "Proceed with structured terms and targeted price protection mechanisms including earnouts or escrow to address identified risk factors.";
      } else if (score >= 50) {
        summary += "Proceed with focused mitigations on the identified risk areas through standard contractual protections.";
      } else {
        summary += "Proceed under standard protections with customary due diligence and representations and warranties.";
      }
      
      return summary;
    };

    // Assemble IC Summary data (Prompt 12)
    const fin = findings.financial || {};
    const icSummaryData = {
      // EBITDA Bridge data
      ebitdaBridge: findings.ebitdaBridge,
      // Customer concentration for EBITDA at risk calculation
      customerConcentration: fin.customerConcentration,
      customerConcentrationPeriod: fin.customerConcentrationPeriod,
      // Working capital data
      workingCapital: {
        dso: fin.dso,
        dsoPeriod: fin.dsoPeriod,
        benchmarkDsoDays: findings.workingCapitalBenchmarkDays || 45, // Default benchmark
      },
      // Net debt for leverage
      netDebt: fin.netDebt,
      netDebtPeriod: fin.netDebtPeriod,
      // QoE EBITDA - handle both array and object formats
      qoeEbitda: (() => {
        // Try to get from ebitdaVariants object first
        if (fin.ebitdaVariants?.qoe_adjusted) {
          return typeof fin.ebitdaVariants.qoe_adjusted === 'object' 
            ? fin.ebitdaVariants.qoe_adjusted.value 
            : fin.ebitdaVariants.qoe_adjusted;
        }
        // Fall back to regular ebitda
        return fin.ebitda;
      })(),
      // Synergy data (from findings or null if not analyzed)
      synergyAnalysis: findings.synergyAnalysis || null,
    };

    return {
      dealId: analysisMode === 'deal' ? selectedTarget : null,
      documentName: targetName,
      analyzedDocumentCount: findings.documentCount || 1,
      lastAnalyzedAtISO: new Date().toISOString(),
      executiveSummary: findings.executiveSummary || generateExecSummary(),
      overall: {
        label: findings.riskLevel === 'high' || findings.riskLevel === 'critical' ? 'HIGH RISK' : 
               findings.riskLevel === 'moderate' || findings.riskLevel === 'medium' ? 'STRUCTURED' :
               findings.riskLevel === 'low' ? 'LOW RISK' : 'ACCEPTABLE',
        score: findings.riskScore || 0,
        confidencePct: confidencePct,
        confidenceLabel: confidencePct < 30 ? 'Low' : confidencePct < 70 ? 'Medium' : 'High'
      },
      counts: {
        red: findings.highRisks?.length || 0,
        orange: findings.mediumRisks?.length || 0,
        low: findings.lowRisks?.length || 0,
        green: findings.greenFlags?.length || 0
      },
      materialDrivers,
      categoryScores,
      items,
      // Clause Detection data
      detectedClauses: findings.detectedClauses || [],
      // Operational Data
      operational: findings.operational || {},
      // IC Summary data for Prompt 12
      icSummary: icSummaryData
    };
  };

  return (
    <div className="aifindings-container">
      <div className="aifindings-header">
        <div className="page-title">
          <div className="page-title-icon">
            <Bot size={20} />
          </div>
          <div className="page-title-text">
            <h1>Due Diligence</h1>
            <span className="page-subtitle">Document analysis, risk detection and quantification</span>
          </div>
        </div>
      </div>

      {/* Analysis Controls */}
      <div className="analysis-controls">
        <div className="control-group">
          <label>Analyze by:</label>
          <div className="mode-toggle">
            <button
              className={`mode-btn ${analysisMode === 'deal' ? 'active' : ''}`}
              onClick={() => {
                setAnalysisMode('deal');
                setSelectedTarget(deals[0]?.id || null);
              }}
            >
              Deal
            </button>
            <button
              className={`mode-btn ${analysisMode === 'document' ? 'active' : ''}`}
              onClick={() => {
                setAnalysisMode('document');
                setSelectedTarget(documents[0]?.jobId || null);
              }}
            >
              Document
            </button>
          </div>
        </div>

        <div className="control-group">
          <label>Select {analysisMode === 'deal' ? 'Deal' : 'Document'}:</label>
          <select
            className="target-select"
            value={selectedTarget || ''}
            onChange={(e) => setSelectedTarget(e.target.value)}
          >
            {analysisMode === 'deal' 
              ? deals.map(deal => (
                  <option key={deal.id} value={deal.id}>{deal.name}</option>
                ))
              : documents.map(doc => (
                  <option key={doc.jobId} value={doc.jobId}>{doc.filename}</option>
                ))
            }
          </select>
        </div>

        {/* Model selection — only for deal analysis; document mode always runs Sonnet */}
        {analysisMode === 'deal' && (
        <div className="control-group">
          <label>AI Model:</label>
          <select
            className="target-select"
            value={modelMode}
            onChange={(e) => setModelMode(e.target.value)}
            style={{ minWidth: 180 }}
          >
            <option value="sonnet">All Sonnet (Best Quality)</option>
            <option value="hybrid">Hybrid Haiku+Sonnet (Balanced)</option>
            <option value="gemini">All Gemini 2.5 Flash (Fastest)</option>
            <option value="deepseek">DeepSeek V4 Flash (Cheap & Fast)</option>
          </select>
        </div>
        )}

        {/* Financial data cleaning toggle — deal mode only. Applies only when the deal
            contains spreadsheet (.xlsx) files; the backend skips it otherwise. */}
        {analysisMode === 'deal' && (
        <div className="control-group control-group--toggle">
          <label>Data Cleaning:</label>
          <button
            type="button"
            role="switch"
            aria-checked={cleaningOn}
            className={`ios-toggle ${cleaningOn ? 'ios-toggle--on' : ''}`}
            onClick={() => setCleaningOn(v => !v)}
            title="Clean & normalize spreadsheet financials before analysis (applies only to deals with .xlsx files)"
          >
            <span className="ios-toggle__thumb" />
          </button>
        </div>
        )}

        {canEdit && <button
          className="btn-analyze"
          onClick={analysisMode === 'deal' ? openIntakeModal : runAnalysis}
          disabled={analyzing || !selectedTarget}
        >
          {analyzing ? (analyzingStage || 'Analyzing...') : hasAnalysis ? 'Re-analyze' : 'Run Analysis'}
        </button>}

        {analysisMode === 'deal' && analysisHistory.length > 1 && (
          <button
            className="btn-history"
            onClick={() => setShowHistory(s => !s)}
          >
            <History size={14} />
            History ({analysisHistory.length})
            {showHistory ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        )}

      </div>

      {showHistory && analysisHistory.length > 1 && (
        <div className="analysis-history-panel">
          <div className="analysis-history-header">Analysis History</div>
          {analysisHistory.map((h, i) => {
            const riskColor = h.overall_risk === 'HIGH' ? '#EF4444' : h.overall_risk === 'MEDIUM' ? '#F59E0B' : '#10B981';
            return (
              <div
                key={h.id}
                className={`analysis-history-row ${(i !== 0 || activeHistoryId) ? 'clickable' : ''} ${activeHistoryId === h.id || (i === 0 && !activeHistoryId) ? 'active' : ''}`}
                onClick={() => {
                  if (loadingHistoryId) return;
                  if (i === 0) { setActiveHistoryId(null); fetchFindings(); setShowHistory(false); }
                  else loadHistoricAnalysis(selectedTarget, h.id);
                }}
              >
                <div className="analysis-history-info">
                  <span className="analysis-history-label">
                    {i === 0 ? 'Latest' : `Run ${analysisHistory.length - i}`}
                  </span>
                  <span className="analysis-history-date">
                    {new Date(h.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {h.created_by_name && <span className="analysis-history-by">· {h.created_by_name}</span>}
                  {h.overall_risk && <span className="analysis-history-risk" style={{ color: riskColor }}>{h.overall_risk}</span>}
                  {h.doc_count > 0 && <span className="analysis-history-docs">{h.doc_count} docs</span>}
                </div>
                {loadingHistoryId === h.id && (
                  <RefreshCw size={12} className="spin" style={{ color: '#6B7280' }} />
                )}
                {(activeHistoryId === h.id || (i === 0 && !activeHistoryId)) && (
                  <span className="analysis-history-current">Viewing</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {analysisError && (
        <div className="analysis-error-banner">
          {analysisError}
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading findings...</p>
        </div>
      ) : !hasAnalysis ? (
        <div className="no-analysis-state">
          <div className="no-analysis-icon"><Search size={32} /></div>
          <p>No analysis performed yet for {getTargetName()}</p>
          <p className="hint">Click "Run Analysis" to generate AI findings.</p>
        </div>
      ) : (
        <>
          {/* Analysis header */}
          <div className="risk-assessment-card">
            <div className="risk-assessment-header">
              <h2>Due Diligence Analysis: {getTargetName()}</h2>
              {findings.ddReport && (() => {
                const flags = findings.ddReport.flags || [];
                const critical = flags.filter(f => f.severity === 'CRITICAL').length;
                const high = flags.filter(f => f.severity === 'HIGH').length;
                const medium = flags.filter(f => f.severity === 'MEDIUM').length;
                return (
                  <div className="flag-summary-badges">
                    {critical > 0 && <span className="flag-badge-critical">{critical} CRITICAL</span>}
                    {high > 0 && <span className="flag-badge-high">{high} HIGH</span>}
                    {medium > 0 && <span className="flag-badge-medium">{medium} MEDIUM</span>}
                  </div>
                );
              })()}
            </div>
            <p className="risk-assessment-description">
              Based on analysis of {findings.documentCount} document{findings.documentCount !== 1 ? 's' : ''}
              {findings.analyzedAt && (
                <span className="analysis-timestamp">
                  {' · '}Analyzed {new Date(findings.analyzedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </p>
          </div>

          {/* Navigation Tabs */}
          <div className="findings-tabs">
            <button
              className={`tab-btn ${activeTab === 'report' ? 'active' : ''}`}
              onClick={() => setActiveTab('report')}
            >
              DD Report
            </button>
            <button
              className={`tab-btn ${activeTab === 'clauses' ? 'active' : ''}`}
              onClick={() => setActiveTab('clauses')}
            >
              Clause Detection
            </button>
            <button
              className={`tab-btn ${activeTab === 'reputation' ? 'active' : ''}`}
              onClick={() => setActiveTab('reputation')}
            >
              Reputational Risk
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'report' && (
              <>
                {console.log('[AIFindings] rendering DiligenceReport, ddReport:', !!findings.ddReport, 'mode:', analysisMode)}
                <DiligenceReport
                  report={findings.ddReport}
                  dealName={getTargetName()}
                  analyzedAt={findings.analyzedAt}
                  onAddToNotes={analysisMode === 'deal' ? openDdNoteModal : undefined}
                />
              </>
            )}
            {activeTab === 'clauses' && renderClauseDetection()}
            {activeTab === 'reputation' && (
              <ReputationRisk
                deal={selectedDeal || deals.find(d => d.id === selectedTarget) || null}
                canEdit={canEdit}
              />
            )}
          </div>
        </>
      )}

      {/* DD Report text → Finding Notes modal */}
      {ddNoteModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }}
          onClick={() => setDdNoteModal(null)}
        >
          <div
            style={{ background: '#fff', borderRadius: 12, padding: 28, width: 520, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>Add to Finding Notes</h3>
              <button onClick={() => setDdNoteModal(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280', lineHeight: 1 }}>×</button>
            </div>
            <form onSubmit={saveDdNote} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Date</label>
                  <input type="date" value={ddNoteForm.date} onChange={e => setDdNoteForm(f => ({ ...f, date: e.target.value }))} required
                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: 2 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Category</label>
                  <select value={ddNoteForm.category} onChange={e => setDdNoteForm(f => ({ ...f, category: e.target.value }))} required
                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}>
                    {DD_NOTE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Key Question (optional)</label>
                <input type="text" value={ddNoteForm.question} onChange={e => setDdNoteForm(f => ({ ...f, question: e.target.value }))}
                  placeholder="e.g., What are the key risks?" style={{ width: '100%', padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Finding *</label>
                <textarea value={ddNoteForm.finding} onChange={e => setDdNoteForm(f => ({ ...f, finding: e.target.value }))} required rows={5}
                  style={{ width: '100%', padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Source Document</label>
                <input type="text" value={ddNoteForm.sourceDocument} onChange={e => setDdNoteForm(f => ({ ...f, sourceDocument: e.target.value }))}
                  style={{ width: '100%', padding: '7px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
                <button type="button" onClick={() => setDdNoteModal(null)}
                  style={{ padding: '8px 18px', border: '1px solid #D1D5DB', borderRadius: 6, background: '#fff', fontSize: 13, cursor: 'pointer', color: '#374151' }}>
                  Cancel
                </button>
                <button type="submit" disabled={ddNoteSaving}
                  style={{ padding: '8px 18px', border: 'none', borderRadius: 6, background: '#1e293b', color: '#fff', fontSize: 13, fontWeight: 600, cursor: ddNoteSaving ? 'not-allowed' : 'pointer', opacity: ddNoteSaving ? 0.7 : 1 }}>
                  {ddNoteSaving ? 'Saving...' : 'Add Finding'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showIntakeModal && intakeData && (
        <DiligenceIntakeModal
          data={intakeData}
          onChange={setIntakeData}
          onConfirm={() => runAnalysis(intakeData)}
          onCancel={() => setShowIntakeModal(false)}
        />
      )}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const STAGE_MAP = {
  teaser: 'Teaser Received',
  ioi: 'Teaser Received',
  cim: 'CIM Received',
  management_presentation: 'Management Presentation',
  loi: 'LOI Stage',
  exclusivity: 'Exclusivity',
  due_diligence: 'Final Diligence',
  final_diligence: 'Final Diligence',
  negotiation: 'Exclusivity',
  closing: 'Post-LOI',
  closed_won: 'Post-LOI',
};

const SIZE_MAP = [
  { max: 10_000_000,   label: 'Under $10M revenue' },
  { max: 50_000_000,   label: '$10M–$50M' },
  { max: 250_000_000,  label: '$50M–$250M' },
  { max: 1_000_000_000,label: '$250M–$1B' },
  { max: Infinity,     label: 'Over $1B' },
];

function buildIntakeDefaults(deal) {
  const stage = deal?.stage ? STAGE_MAP[deal.stage.toLowerCase()] || '' : '';
  const dealType = deal?.dealType || '';

  // Match deal industry to one of the sector options (case-insensitive)
  const industryRaw = deal?.industry || '';
  const sector = SECTORS.find(s => s.toLowerCase() === industryRaw.toLowerCase())
    || SECTORS.find(s => industryRaw.toLowerCase().includes(s.toLowerCase()))
    || industryRaw
    || '';

  let size = '';
  if (deal?.entryRevenue) {
    const rev = Number(deal.entryRevenue);
    const bucket = SIZE_MAP.find(b => rev <= b.max);
    if (bucket) size = bucket.label;
  }

  return {
    dealType,
    stage,
    reviewerRole: '',
    goals: ['Find all flags', 'Validate EBITDA quality', 'Assess legal & contract risk'],
    outputFormat: 'Full structured report',
    sector,
    size,
    additionalContext: '',
  };
}

// ── Intake Modal ─────────────────────────────────────────────────────────────

const DEAL_TYPES = ['Acquisition', 'Minority Investment', 'Growth Equity', 'Recapitalization', 'Merger', 'Secondary'];
const STAGES = ['Teaser Received', 'CIM Received', 'Management Presentation', 'LOI Stage', 'Exclusivity', 'Final Diligence', 'Post-LOI'];
const ROLES = ['Partner', 'Principal', 'Associate', 'VP', 'Operating Partner', 'Portfolio Company Management', 'Legal Counsel'];
const GOALS = ['Find all flags', 'Validate EBITDA quality', 'Assess management team', 'Assess legal & contract risk', 'Build financial model inputs', 'Prepare for management presentation', 'Identify deal-breakers only'];
const OUTPUT_FORMATS = ['Full structured report', 'Flag register only', 'Executive summary only', 'Section-by-section breakdown'];
const SECTORS = ['Environmental Services', 'Healthcare', 'Technology', 'Business Services', 'Industrials', 'Consumer', 'Real Estate', 'Other'];
const SIZES = ['Under $10M revenue', '$10M–$50M', '$50M–$250M', '$250M–$1B', 'Over $1B'];

function ToggleGroup({ options, value, onChange, multi = false }) {
  const toggle = (opt) => {
    if (multi) {
      onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]);
    } else {
      onChange(value === opt ? '' : opt);
    }
  };
  return (
    <div className="intake-toggle-group">
      {options.map(opt => (
        <button
          key={opt}
          type="button"
          className={`intake-toggle-btn ${(multi ? value.includes(opt) : value === opt) ? 'selected' : ''}`}
          onClick={() => toggle(opt)}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function DiligenceIntakeModal({ data, onChange, onConfirm, onCancel }) {
  const set = (key) => (val) => onChange(prev => ({ ...prev, [key]: val }));

  return (
    <div className="intake-modal-overlay" onClick={onCancel}>
      <div className="intake-modal" onClick={e => e.stopPropagation()}>
        <div className="intake-modal-header">
          <h2>Configure Due Diligence Analysis</h2>
        </div>

        <div className="intake-modal-body">
          <div className="intake-section">
            <label>Deal Type</label>
            <ToggleGroup options={DEAL_TYPES} value={data.dealType} onChange={set('dealType')} />
          </div>

          <div className="intake-section">
            <label>Deal Stage</label>
            <ToggleGroup options={STAGES} value={data.stage} onChange={set('stage')} />
          </div>

          <div className="intake-section">
            <label>Your Role</label>
            <ToggleGroup options={ROLES} value={data.reviewerRole} onChange={set('reviewerRole')} />
          </div>

          <div className="intake-section">
            <label>Primary Goals <span className="intake-multi-hint">(select all that apply)</span></label>
            <ToggleGroup options={GOALS} value={data.goals} onChange={set('goals')} multi />
          </div>

          <div className="intake-section">
            <label>Output Format</label>
            <ToggleGroup options={OUTPUT_FORMATS} value={data.outputFormat} onChange={set('outputFormat')} />
          </div>

          <div className="intake-row">
            <div className="intake-section">
              <label>Sector</label>
              <ToggleGroup options={SECTORS} value={data.sector} onChange={set('sector')} />
            </div>
            <div className="intake-section">
              <label>Target Company Size</label>
              <ToggleGroup options={SIZES} value={data.size} onChange={set('size')} />
            </div>
          </div>

          <div className="intake-section">
            <label>Anything else we should know? <span className="intake-multi-hint">(optional)</span></label>
            <textarea
              className="intake-textarea"
              placeholder="e.g. Known litigation, difficult founder, specific thesis being tested, areas of particular concern…"
              value={data.additionalContext}
              onChange={e => set('additionalContext')(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <div className="intake-modal-footer">
          <button className="intake-btn-cancel" onClick={onCancel}>Cancel</button>
          <button className="intake-btn-confirm" onClick={onConfirm}>
            Run Analysis
          </button>
        </div>
      </div>
    </div>
  );
}

export default AIFindings;
